import type {
  ActiveFireFeatureCollection,
  AircraftDetail,
  AircraftFeatureCollection,
  CameraFeatureCollection,
  CommunityFeatureProps,
  Community,
  CommunityCreate,
  CommunityPatch,
  CommunityDetail,
  CommunityMessage,
  CommunityCoordinationInboxRow,
  CommunityHeadcount,
  CommunityHeadcountListResponse,
  CommunityWellnessCheck,
  CommunityWellnessCheckListResponse,
  ArrivalAttestationsResponse,
  HeadcountCreate,
  WellnessCheckCreate,
  WellnessCheckPatch,
  DispatchRecommendation,
  DispatchRecommendationRequest,
  Incident,
  IncidentDetail,
  IncidentLifecycleState,
  OwnedIncidentRef,
  AarReplayResponse,
  IncidentSourceRecordType,
  AreaCommandProposal,
  LinkableIncident,
  ProtectiveActionKind,
  ProtectiveActionLinkResponse,
  OperationalPeriod,
  OperationalPeriodStatus,
  HighRiskHotspotDetail,
  HighRiskHotspotFeatureCollection,
  RawHotspotDetail,
  RawHotspotFeatureCollection,
  CameraSmokeDetail,
  CameraSmokeFeatureCollection,
  UtilityOutageDetail,
  UtilityOutageFeatureCollection,
  RoadClosureDetail,
  RoadClosureFeatureCollection,
  ArcgisFeatureCollection,
  ArcgisFeatureDetail,
  EvacOrderLinks,
  EntityEvacOrders,
  CommsDegradationZoneFeatureCollection,
  CapAlertDetail,
  CapAlertFeatureCollection,
  NearbyAircraft,
  NearbyAsset,
  ResponseAssetDetail,
  ResponseAssetFeatureCollection,
  SensorFeatureCollection,
  SitrepListResponse,
  SitrepReport,
  SitrepRequest,
  ShiftLogSummary,
  ShiftLogInclude,
  AssistanceRequest,
  AssistanceRequestCreate,
  AssistanceRequestListResponse,
  AssistanceStatusUpdate,
  AssistanceStatus,
  AssistanceUrgency,
  IncidentLinkType,
  DeliveryResult,
  DeliveryDestination,
  ResourceRequest,
  ResourceRequestCreate,
  ResourceRequestListResponse,
  ResourceRequestStatus,
  ResourceUrgency,
  StagingArea,
  StagingAreaCreate,
  StagingAreaListResponse,
  BriefingSnapshot,
  BriefingToken,
  BriefingTokenCreate,
  BriefingSubscription,
  BriefingSubscriptionCreate,
  BriefOfficialRequest,
  BriefOfficialResult,
  RecordOfficialResponseRequest,
  RecordOfficialResponseResult,
  OfficialBriefingHistoryResponse,
  Sector,
  SectorCreate,
  SectorDetail,
  SectorListResponse,
  OperatorEvacuationOrderCreate,
  OperatorEvacuationOrderGeometryPatch,
  OperatorEvacuationOrderMetadataPatch,
  OperatorEvacuationOrderStartAccounting,
  OperatorEvacuationOrderStartAccountingResult,
  OperatorEvacOrderFeature,
  OperatorEvacOrderFeatureCollection,
  CommunityEvacuationZone,
  Polygon,
  EvacuationSurface,
  SearchResponse,
  FeedHealthResponse,
  UserRole,
  AgencyKind,
  OperatorAuthLevel,
  OperatorCredentialStatus,
  CredentialExpiryFlag,
  DronePlatformCategory,
  DroneDownlinkType,
  DroneSensorType,
  DroneAvailabilityStatus,
  DroneReadiness,
  DroneRequestUrgency,
  DroneFeedType,
  DroneMatchRequestInput,
  DroneMatchResponse,
  RateCard,
  OperatorPricingTier,
  OperatorKind,
  MissionStatus,
  MissionFeedDescriptor,
  AgencyTaskView,
  AgencyView,
  CommandHandoverInboxItem,
  ResourceLifecycleState,
  NotificationSubscription,
  NotificationSubscriptionCreate,
  NotificationSubscriptionPatch,
  EmergencyDeclarationRecord,
  EmergencyDeclarationCreate,
  EmergencyDeclarationNotifyResult,
  EmergencyDeclarationDualNotifyResult,
  DeclarationAuthorityKind,
  PublicAlertRequestRecord,
  PublicAlertRequestCreate,
  PublicAlertRequestResult,
  AirSupportRequestRecord,
  AirSupportRequestCreate,
  AirSupportRequestResult,
  AirSupportAuthorityKind,
  AirSupportRequestedResource,
  PublicInfoPublishRequest,
  PublicInfoPublishResult,
  SitrepDraftResult,
  PeocArrival,
  DeskWatchState,
  WatchSourceType,
  InteropsContextType,
  InteropsMessageKind,
  InteropsMessageView,
  InteropsThreadView,
  InteropsRollupView,
  AfterActionResponse,
  AfterActionEntryKind,
} from '@tsr/shared-types';
import { parseUserRoleOrViewer } from '@tsr/shared-types';
import { getSeatName } from './lib/seatIdentity';

/**
 * The 401 handler the AuthProvider registers (RBAC R.2b, #314). `authedFetch` calls
 * it when a NON-auth request 401s — i.e. the session lapsed or was revoked
 * mid-session — so the app can drop to a logged-out state (and the root gate shows
 * the login screen when enforcement is on). A no-op until registered, which keeps
 * this module free of any React dependency.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/**
 * Central authed-fetch chokepoint (RBAC R.1, #313; credentialed at R.2b, #314) — the
 * single place the web app attaches operator identity to an API call. It forwards the
 * desk's seat name as the `x-operator-id` header (byte-identical to the inline
 * call-sites it replaces) and now sends the session cookie with every call.
 *
 * `credentials: 'include'` so the signed, HttpOnly session cookie rides EVERY request,
 * including cross-origin (a same-origin call already sends it; a deployed split-origin
 * web/api would not without this). On a 401 to a non-auth path it notifies the
 * registered handler (→ logged-out state / login gate). The `/api/auth/` endpoints
 * self-handle their own 401s (login bad-credentials, the /me probe), so they're
 * excluded to avoid a re-fetch loop.
 *
 * Headers are merged as a plain object so callers keep their existing
 * `{ 'Content-Type': ... }` shape; the seat header is attached only when a seat is
 * set (mirrors the old `...(seat ? { 'x-operator-id': seat } : {})`).
 */
export async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const seat = getSeatName();
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
    ...(seat ? { 'x-operator-id': seat } : {}),
  };
  const res = await fetch(input, { credentials: 'include', ...init, headers });
  if (res.status === 401 && !input.startsWith('/api/auth/')) onUnauthorized?.();
  return res;
}

/* ------------------------------------------------------------------ *
 * Auth surface (RBAC R.2a backend #314 / R.2b web). The login/session contract:
 *   GET  /api/auth/config → { authEnforced }           (public — drives the gate)
 *   GET  /api/auth/me     → { id, email, display_name, role } | 401
 *   POST /api/auth/login  → 200 { display_name } + Set-Cookie | 401 (generic)
 *   POST /api/auth/logout → 204 (revokes + clears the cookie)
 * ------------------------------------------------------------------ */

export interface AuthConfig {
  authEnforced: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  /** Account role (RBAC R.3a, #315; widened to `UserRole` for the external-operator
   *  portal class in Drone D1 #316). Read-only on the client — the SERVER is the gate;
   *  surfaced for display (CommandBar) and to route a non-COP principal (a
   *  `drone-operator`) to the Operator-Mode shell instead of the COP. */
  role: UserRole;
  /** The acting seat's agency (tenant) — RBAC Phase 1 (#566). Drives the persistent
   *  "Signed in as <name> · <Agency> · <Role>" identity chip. OPTIONAL/nullable to
   *  tolerate an older server that predates the /api/auth/me agency join (and test mocks
   *  that predate this field). */
  agency?: { id: string; name: string; kind: AgencyKind | null } | null;
}

/** The server's enforcement flag — the single source of truth for whether the web
 *  gates. Ships false (R.2b); R.2c flips it server-side. */
export async function fetchAuthConfig(signal?: AbortSignal): Promise<AuthConfig> {
  const res = await authedFetch('/api/auth/config', { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`fetchAuthConfig failed ${res.status}`);
  return (await res.json()) as AuthConfig;
}

/** The current operator from the session, or null when logged out (a 401 is the
 *  normal unauthenticated state, not an error). */
export async function fetchAuthMe(signal?: AbortSignal): Promise<AuthUser | null> {
  const res = await authedFetch('/api/auth/me', { signal, cache: 'no-store' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`fetchAuthMe failed ${res.status}`);
  const data = (await res.json()) as {
    id: string;
    email: string;
    display_name: string;
    role?: unknown;
    agency?: { id: string; name: string; kind: AgencyKind | null } | null;
  };
  // parseUserRoleOrViewer tolerates an absent/unknown role (older server) → least
  // privilege, but preserves a 'drone-operator' value so the gate can route a
  // non-COP principal to the Operator-Mode shell (never the COP).
  return {
    id: data.id,
    email: data.email,
    displayName: data.display_name,
    role: parseUserRoleOrViewer(data.role),
    agency: data.agency ?? null,
  };
}

/* ------------------------------------------------------------------ *
 * Operator-portal surface (Drone D1 #316) — the only API surface a non-COP
 * (external drone-operator) principal may reach. The server class gate 403s such a
 * principal on every COP route. #316 scaffolds only the identity endpoint; the real
 * portal (onboarding / compliance / assets / feed upload) is #318.
 *   GET /api/operator-portal/me → { operator, organization } | 401
 * ------------------------------------------------------------------ */

export interface OperatorPortalMe {
  operator: { id: string; displayName: string | null; role: UserRole };
  organization: { id: string; name: string; kind: AgencyKind | null } | null;
}

/** The authenticated operator's own org/identity, or null when unauthenticated. */
export async function fetchOperatorPortalMe(
  signal?: AbortSignal,
): Promise<OperatorPortalMe | null> {
  const res = await authedFetch('/api/operator-portal/me', { signal, cache: 'no-store' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`fetchOperatorPortalMe failed ${res.status}`);
  const data = (await res.json()) as {
    operator: { id: string; display_name: string | null; role?: unknown };
    organization: { id: string; name: string; kind: AgencyKind | null } | null;
  };
  return {
    operator: {
      id: data.operator.id,
      displayName: data.operator.display_name,
      role: parseUserRoleOrViewer(data.operator.role),
    },
    organization: data.organization,
  };
}

/* ------------------------------------------------------------------ *
 * Operator-portal CRUD (Drone D1 #318) — the operator's OWN compliance + fleet, every
 * call scoped server-side to req.operator.agencyId. The web is a thin client over the
 * portal API; the SERVER is the isolation wall. All paths go through authedFetch.
 * ------------------------------------------------------------------ */

export interface OperatorOrgProfile {
  id: string;
  name: string;
  kind: AgencyKind | null;
}

/** The operator profile read: the OWN org + the acting pilot's OWN call-back phone (#346). */
export interface OperatorProfileView {
  organization: OperatorOrgProfile;
  phone: string | null;
}

export interface OperatorCredential {
  id: string;
  auth_level: OperatorAuthLevel;
  label: string;
  credential_ref: string | null;
  issued_at: string | null;
  expires_at: string | null;
  status: OperatorCredentialStatus;
  expiry_flag: CredentialExpiryFlag;
  created_at: string;
  updated_at: string;
}

export interface OperatorCredentialInput {
  auth_level: OperatorAuthLevel;
  label: string;
  credential_ref?: string | null;
  issued_at?: string | null;
  expires_at?: string | null;
}

export interface DroneAssetView {
  id: string;
  name: string;
  tail_number: string | null;
  platform_category: DronePlatformCategory;
  downlink_type: DroneDownlinkType | null;
  home_base: { lat: number; lon: number } | null;
  service_radius_km: number | null;
  status: string;
  availability_status: DroneAvailabilityStatus;
  on_call: boolean;
  illustrative: boolean;
  sensors: DroneSensorType[];
  created_at: string;
  updated_at: string;
}

export interface DroneAssetInput {
  name: string;
  tail_number?: string | null;
  platform_category: DronePlatformCategory;
  downlink_type?: DroneDownlinkType | null;
  home_base?: { lat: number; lon: number } | null;
  service_radius_km?: number | null;
  sensors?: DroneSensorType[];
}

async function portalJson<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) throw new Error(`${label} failed ${res.status}`);
  return (await res.json()) as T;
}

const portalPost = (url: string, body: unknown): Promise<Response> =>
  authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
const portalPatch = (url: string, body: unknown): Promise<Response> =>
  authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export async function fetchOperatorProfile(
  signal?: AbortSignal,
): Promise<OperatorProfileView | null> {
  const res = await authedFetch('/api/operator-portal/profile', { signal, cache: 'no-store' });
  if (res.status === 401) return null;
  const data = await portalJson<{ organization?: OperatorOrgProfile; phone?: string | null }>(
    res,
    'fetchOperatorProfile',
  );
  if (!data.organization) return null;
  return { organization: data.organization, phone: data.phone ?? null };
}

/** Update the operator's OWN org name and/or the acting pilot's OWN call-back phone (#346).
 *  Pass `phone: null` to clear it. At least one field must be present. */
export async function updateOperatorProfile(
  input: { name?: string; phone?: string | null },
  signal?: AbortSignal,
): Promise<OperatorProfileView> {
  const res = await portalPatch('/api/operator-portal/profile', input);
  void signal;
  const data = await portalJson<{ organization: OperatorOrgProfile; phone?: string | null }>(
    res,
    'updateOperatorProfile',
  );
  return { organization: data.organization, phone: data.phone ?? null };
}

export async function fetchOperatorCredentials(
  signal?: AbortSignal,
): Promise<OperatorCredential[]> {
  const res = await authedFetch('/api/operator-portal/credentials', { signal, cache: 'no-store' });
  if (res.status === 401) return [];
  const data = await portalJson<{ credentials?: OperatorCredential[] }>(
    res,
    'fetchOperatorCredentials',
  );
  return data.credentials ?? [];
}

export async function createOperatorCredential(
  input: OperatorCredentialInput,
): Promise<OperatorCredential> {
  const data = await portalJson<{ credential: OperatorCredential }>(
    await portalPost('/api/operator-portal/credentials', input),
    'createOperatorCredential',
  );
  return data.credential;
}

export async function updateOperatorCredential(
  id: string,
  patch: Partial<OperatorCredentialInput> & { status?: OperatorCredentialStatus },
): Promise<OperatorCredential> {
  const data = await portalJson<{ credential: OperatorCredential }>(
    await portalPatch(`/api/operator-portal/credentials/${id}`, patch),
    'updateOperatorCredential',
  );
  return data.credential;
}

export async function fetchOperatorAssets(signal?: AbortSignal): Promise<DroneAssetView[]> {
  const res = await authedFetch('/api/operator-portal/assets', { signal, cache: 'no-store' });
  if (res.status === 401) return [];
  const data = await portalJson<{ assets?: DroneAssetView[] }>(res, 'fetchOperatorAssets');
  return data.assets ?? [];
}

export async function createOperatorAsset(input: DroneAssetInput): Promise<DroneAssetView> {
  const data = await portalJson<{ asset: DroneAssetView }>(
    await portalPost('/api/operator-portal/assets', input),
    'createOperatorAsset',
  );
  return data.asset;
}

export async function updateOperatorAsset(
  id: string,
  patch: Partial<DroneAssetInput> & { status?: 'active' | 'retired' },
): Promise<DroneAssetView> {
  const data = await portalJson<{ asset: DroneAssetView }>(
    await portalPatch(`/api/operator-portal/assets/${id}`, patch),
    'updateOperatorAsset',
  );
  return data.asset;
}

export async function updateOperatorAssetAvailability(
  id: string,
  patch: { availability_status?: DroneAvailabilityStatus; on_call?: boolean },
): Promise<DroneAssetView> {
  const data = await portalJson<{ asset: DroneAssetView }>(
    await portalPatch(`/api/operator-portal/assets/${id}/availability`, patch),
    'updateOperatorAssetAvailability',
  );
  return data.asset;
}

/** The operator's OWN agency-level rate card + its derived pricing tier (#344). For a
 *  government_owned operator the rates read back null + tier 'gov' (the form is read-only). */
export interface RateCardView {
  rate_card: RateCard;
  government_owned: boolean;
  operator_kind: OperatorKind | null;
  pricing_tier: OperatorPricingTier;
}

export async function fetchOperatorRateCard(signal?: AbortSignal): Promise<RateCardView | null> {
  const res = await authedFetch('/api/operator-portal/rate-card', { signal, cache: 'no-store' });
  if (res.status === 401 || res.status === 404) return null;
  return portalJson<RateCardView>(res, 'fetchOperatorRateCard');
}

/** Update the operator's OWN rate card (any subset; send null to clear a field). A
 *  government_owned operator is rejected server-side (400) — they are $0-rated. */
export async function updateOperatorRateCard(patch: {
  mobilization_fee_cad?: number | null;
  hourly_rate_cad?: number | null;
  per_km_cad?: number | null;
}): Promise<RateCardView> {
  return portalJson<RateCardView>(
    await portalPatch('/api/operator-portal/rate-card', patch),
    'updateOperatorRateCard',
  );
}

export type LoginResult = { ok: true; displayName: string } | { ok: false };

/** Attempt a login. Any failure (400 validation / 401 bad-credentials / inactive)
 *  collapses to a single generic `{ ok: false }` — no account enumeration, mirroring
 *  the server's one generic 401. */
export async function postLogin(
  email: string,
  password: string,
  signal?: AbortSignal,
): Promise<LoginResult> {
  const res = await authedFetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
    signal,
  });
  if (res.status === 200) {
    const data = (await res.json()) as { display_name: string };
    return { ok: true, displayName: data.display_name };
  }
  return { ok: false };
}

/** Revoke the session + clear the cookie. Idempotent (safe with no session). */
export async function postLogout(signal?: AbortSignal): Promise<void> {
  await authedFetch('/api/auth/logout', { method: 'POST', signal });
}

export interface BboxQuery {
  west: number;
  south: number;
  east: number;
  north: number;
}

function bboxParam(b: BboxQuery): string {
  return `${b.west},${b.south},${b.east},${b.north}`;
}

/** National bounding box covering all of Canada (with a small margin). The
 *  Anomaly Queue's "Show all Canada" decoupling (#146) widens the queue to this
 *  envelope so a red outside the current map frame is never silently hidden. */
export const CANADA_BBOX: BboxQuery = { west: -141.5, south: 41.0, east: -52.0, north: 83.5 };

export async function fetchSensors(
  bbox: BboxQuery,
  signal: AbortSignal,
): Promise<SensorFeatureCollection> {
  const res = await fetch(`/api/sensors?bbox=${bboxParam(bbox)}`, { signal });
  if (!res.ok) throw new Error(`fetchSensors failed ${res.status}`);
  return (await res.json()) as SensorFeatureCollection;
}

export interface CameraFetchOpts {
  smokePriority?: boolean;
  /** Operator-curated wall pool only (`wall_featured=true`). The wall unions
   *  this with the smoke-priority set so a featured scenic cam reaches the wall
   *  even when it isn't smoke-priority (#362; featured ⇒ wall-visible). */
  featured?: boolean;
}

export async function fetchCameras(
  bbox: BboxQuery,
  signal: AbortSignal,
  opts: CameraFetchOpts = {},
): Promise<CameraFeatureCollection> {
  // Limit must cover the whole fleet in view: at country zoom ~6.1k cameras
  // intersect the bbox, and the old 6000 cap silently dropped ~149 of them
  // (QA-8). 10000 is the API's max; the server also ORDER BYs live-first so any
  // future overflow drops least-important cams deterministically, not at random.
  const params = new URLSearchParams({ bbox: bboxParam(bbox), limit: '10000' });
  if (opts.smokePriority) params.set('smoke_priority', 'true');
  if (opts.featured) params.set('featured', 'true');
  const res = await fetch(`/api/cameras?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchCameras failed ${res.status}`);
  return (await res.json()) as CameraFeatureCollection;
}

export async function fetchResponseAssets(
  bbox: BboxQuery,
  assetClasses: string[],
  signal: AbortSignal,
): Promise<ResponseAssetFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox), limit: '8000' });
  if (assetClasses.length > 0) params.set('asset_class', assetClasses.join(','));
  const res = await fetch(`/api/response-assets?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchResponseAssets failed ${res.status}`);
  return (await res.json()) as ResponseAssetFeatureCollection;
}

export async function fetchResponseAsset(
  id: string,
  signal?: AbortSignal,
): Promise<ResponseAssetDetail> {
  const res = await fetch(`/api/response-assets/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchResponseAsset failed ${res.status}`);
  return (await res.json()) as ResponseAssetDetail;
}

export interface ActiveFireFetchOpts {
  /** Optional canonical-stage filter (OC/BH/UC/OUT). Absent → all stages. */
  stages?: string[];
  /** Optional response_type filter (FUL/MOD/MON). Absent → all response types.
   *  #607/#621: the Default OC Layer 4 passes NO responseTypes (only stage ['OC']) — every
   *  OC posture reaches the client and the LOD governs visibility. Only fixtures/tests pass one. */
  responseTypes?: string[];
  /** #580 — opt into the server-side Canada scope (the operator queue passes it so its
   *  rows are Canada-scoped like the map). The fire map-layer fetch omits it. */
  canadaScope?: boolean;
  /** #580 — opt into the OC-fire freshness gate (drop an implausibly-stale fixture fire
   *  from the queue + oldest-age metric). Queue-only; the map fetch omits it (FROZEN disc). */
  freshOnly?: boolean;
}

export async function fetchActiveFires(
  bbox: BboxQuery,
  signal: AbortSignal,
  opts: ActiveFireFetchOpts = {},
): Promise<ActiveFireFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox) });
  if (opts.stages && opts.stages.length > 0) params.set('stage', opts.stages.join(','));
  if (opts.responseTypes && opts.responseTypes.length > 0)
    params.set('responseType', opts.responseTypes.join(','));
  if (opts.canadaScope) params.set('canada_scope', '1');
  if (opts.freshOnly) params.set('fresh_only', '1');
  const res = await fetch(`/api/active-fires?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchActiveFires failed ${res.status}`);
  return (await res.json()) as ActiveFireFeatureCollection;
}

export interface HighRiskHotspotFetchOpts {
  /** Minimum confidence (0–100) to return. Defaults to 60 (the F.1.1 floor). */
  minConfidence?: number;
  /** Optional ISO recency lower bound (#147 "since shift start" view). */
  since?: string;
  /** IMS Response-Level band filter (#370 de-flood). The urgent COP layer sends
   *  minResponseLevel=3 (gated reds); the fusion-candidates layer sends
   *  maxResponseLevel=2 (demoted L1/L2). Absent on the panel/wall (full L1–L4). */
  minResponseLevel?: number;
  maxResponseLevel?: number;
  /** #580 — opt into the server-side Canada scope (operator queue only; map fetch omits it). */
  canadaScope?: boolean;
}

export async function fetchHighRiskHotspots(
  bbox: BboxQuery,
  signal: AbortSignal,
  opts: HighRiskHotspotFetchOpts = {},
): Promise<HighRiskHotspotFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox) });
  params.set('min_confidence', String(opts.minConfidence ?? 60));
  if (opts.since) params.set('since', opts.since);
  if (opts.minResponseLevel != null)
    params.set('min_response_level', String(opts.minResponseLevel));
  if (opts.maxResponseLevel != null)
    params.set('max_response_level', String(opts.maxResponseLevel));
  if (opts.canadaScope) params.set('canada_scope', '1');
  const res = await fetch(`/api/high-risk-hotspots?${params.toString()}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`fetchHighRiskHotspots failed ${res.status}`);
  return (await res.json()) as HighRiskHotspotFeatureCollection;
}

export async function fetchHighRiskHotspotDetail(
  id: string,
  signal?: AbortSignal,
): Promise<HighRiskHotspotDetail> {
  const res = await fetch(`/api/high-risk-hotspots/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchHighRiskHotspotDetail failed ${res.status}`);
  return (await res.json()) as HighRiskHotspotDetail;
}

/* ------------------------------------------------------------------ *
 * E.W.0.5 — RAW (pre-fusion) satellite thermal anomalies. The lower-confidence
 * sibling of the fused high-risk hotspots; served from hotspot_detections.
 * ------------------------------------------------------------------ */

export interface RawHotspotFetchOpts {
  /** Lookback window in hours (server default 24, cap 168). */
  hours?: number;
  /** Minimum numeric confidence 0–100 (server default 0 = all confidences). */
  minConfidence?: number;
  /** Restrict to one ingest source (firms-nrt / cwfis-m3 / goes-r-fdcc). */
  source?: string;
}

export async function fetchRawHotspots(
  bbox: BboxQuery,
  signal: AbortSignal,
  opts: RawHotspotFetchOpts = {},
): Promise<RawHotspotFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox) });
  if (opts.hours != null) params.set('hours', String(opts.hours));
  if (opts.minConfidence != null) params.set('min_confidence', String(opts.minConfidence));
  if (opts.source) params.set('source', opts.source);
  const res = await fetch(`/api/hotspots/raw?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchRawHotspots failed ${res.status}`);
  return (await res.json()) as RawHotspotFeatureCollection;
}

export async function fetchRawHotspotDetail(
  id: string,
  signal?: AbortSignal,
): Promise<RawHotspotDetail> {
  const res = await fetch(`/api/hotspots/raw/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchRawHotspotDetail failed ${res.status}`);
  return (await res.json()) as RawHotspotDetail;
}

/* ------------------------------------------------------------------ *
 * E.W.5 — Camera Smoke Detections. The PRE-FUSION camera-CV sibling of the raw
 * hotspots; served from camera_smoke_detections (confirm-stage positives),
 * anchored to the camera's location.
 * ------------------------------------------------------------------ */

export interface CameraSmokeFetchOpts {
  /** Lookback window in hours (server default 72, cap 336). */
  hours?: number;
  /** Minimum confirm-stage confidence 0–100 (server default 0 = all). */
  minConfidence?: number;
}

export async function fetchCameraSmoke(
  bbox: BboxQuery,
  signal: AbortSignal,
  opts: CameraSmokeFetchOpts = {},
): Promise<CameraSmokeFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox) });
  if (opts.hours != null) params.set('hours', String(opts.hours));
  if (opts.minConfidence != null) params.set('min_confidence', String(opts.minConfidence));
  const res = await fetch(`/api/camera-smoke-detections?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchCameraSmoke failed ${res.status}`);
  return (await res.json()) as CameraSmokeFeatureCollection;
}

export async function fetchCameraSmokeDetail(
  id: string,
  signal?: AbortSignal,
): Promise<CameraSmokeDetail> {
  const res = await fetch(`/api/camera-smoke-detections/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchCameraSmokeDetail failed ${res.status}`);
  return (await res.json()) as CameraSmokeDetail;
}

/* ------------------------------------------------------------------ *
 * Power Outages (Ice Storm Risk) — the ambient utility_outages render. Default
 * active-only (the API filters status IN active/crew_dispatched); an optional
 * status CSV overrides it.
 * ------------------------------------------------------------------ */

export interface UtilityOutageFetchOpts {
  /** Freshness window in hours (server default 72, cap 336). */
  hours?: number;
  /** Override the active-only default with an explicit status set (CSV / array). */
  status?: string | string[];
}

export async function fetchUtilityOutages(
  bbox: BboxQuery,
  signal: AbortSignal,
  opts: UtilityOutageFetchOpts = {},
): Promise<UtilityOutageFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox) });
  if (opts.hours != null) params.set('hours', String(opts.hours));
  if (opts.status) {
    params.set('status', Array.isArray(opts.status) ? opts.status.join(',') : opts.status);
  }
  const res = await fetch(`/api/utility-outages?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchUtilityOutages failed ${res.status}`);
  return (await res.json()) as UtilityOutageFeatureCollection;
}

export async function fetchUtilityOutageDetail(
  id: string,
  signal?: AbortSignal,
): Promise<UtilityOutageDetail> {
  const res = await fetch(`/api/utility-outages/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchUtilityOutageDetail failed ${res.status}`);
  return (await res.json()) as UtilityOutageDetail;
}

/* ------------------------------------------------------------------ *
 * Road Closures (Ice Storm Risk → "Road Closures"; R.1f road_closures).
 * ------------------------------------------------------------------ */

export interface RoadClosureFetchOpts {
  /** Freshness window in hours (server default 72, cap 336). */
  hours?: number;
  /** Override the active-only default with an explicit status set (CSV / array). */
  status?: string | string[];
}

export async function fetchRoadClosures(
  bbox: BboxQuery,
  signal: AbortSignal,
  opts: RoadClosureFetchOpts = {},
): Promise<RoadClosureFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox) });
  if (opts.hours != null) params.set('hours', String(opts.hours));
  if (opts.status) {
    params.set('status', Array.isArray(opts.status) ? opts.status.join(',') : opts.status);
  }
  const res = await fetch(`/api/road-closures?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchRoadClosures failed ${res.status}`);
  return (await res.json()) as RoadClosureFeatureCollection;
}

export async function fetchRoadClosureDetail(
  id: string,
  signal?: AbortSignal,
): Promise<RoadClosureDetail> {
  const res = await fetch(`/api/road-closures/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchRoadClosureDetail failed ${res.status}`);
  return (await res.json()) as RoadClosureDetail;
}

/** #204 — ArcGIS public features (default: BC evacuation orders & alerts).
 *  Geometry is simplified server-side; `category` defaults to 'evacuation'. */
export async function fetchArcgisFeatures(
  bbox: BboxQuery,
  signal: AbortSignal,
  category = 'evacuation',
): Promise<ArcgisFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox), category });
  const res = await fetch(`/api/arcgis-features?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchArcgisFeatures failed ${res.status}`);
  return (await res.json()) as ArcgisFeatureCollection;
}

export async function fetchArcgisFeatureDetail(
  id: string,
  signal?: AbortSignal,
): Promise<ArcgisFeatureDetail> {
  const res = await fetch(`/api/arcgis-features/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchArcgisFeatureDetail failed ${res.status}`);
  return (await res.json()) as ArcgisFeatureDetail;
}

/** #206 — an evac order's spatial cross-links (incidents it threads to + the
 *  communities its polygon covers). Empty arrays = no link (the render gate). */
export async function fetchEvacOrderLinks(
  featureId: string,
  signal?: AbortSignal,
): Promise<EvacOrderLinks> {
  const res = await fetch(`/api/arcgis-features/${featureId}/links`, { signal });
  if (!res.ok) throw new Error(`fetchEvacOrderLinks failed ${res.status}`);
  return (await res.json()) as EvacOrderLinks;
}

/** #206 — evac orders whose polygon covers this community. */
export async function fetchCommunityEvacOrders(
  communityId: string,
  signal?: AbortSignal,
): Promise<EntityEvacOrders> {
  const res = await fetch(`/api/communities/${communityId}/evacuation-orders`, { signal });
  if (!res.ok) throw new Error(`fetchCommunityEvacOrders failed ${res.status}`);
  return (await res.json()) as EntityEvacOrders;
}

/** #206 — evac orders covering any community linked to this incident (§4.4 edge). */
export async function fetchIncidentEvacOrders(
  incidentId: string,
  signal?: AbortSignal,
): Promise<EntityEvacOrders> {
  const res = await fetch(`/api/incidents/${incidentId}/evacuation-orders`, { signal });
  if (!res.ok) throw new Error(`fetchIncidentEvacOrders failed ${res.status}`);
  return (await res.json()) as EntityEvacOrders;
}

/** #109 — fusion-derived estimated comms-degradation zones (bbox-filtered). */
export async function fetchCommsDegradationZones(
  bbox: BboxQuery,
  signal: AbortSignal,
): Promise<CommsDegradationZoneFeatureCollection> {
  const res = await fetch(`/api/comms-degradation-zones?bbox=${bboxParam(bbox)}`, { signal });
  if (!res.ok) throw new Error(`fetchCommsDegradationZones failed ${res.status}`);
  return (await res.json()) as CommsDegradationZoneFeatureCollection;
}

/** Build a `bbox`(+optional `since`) query string for the simple queue endpoints.
 *  The bbox is left unencoded (commas intact) to preserve the existing wire format. */
function anomalyListQuery(bbox: BboxQuery, since?: string): string {
  const base = `bbox=${bboxParam(bbox)}`;
  return since ? `${base}&since=${encodeURIComponent(since)}` : base;
}

/** Active Severe/Extreme CAP polygons in view (EWW / L.0 Layer #2 blending).
 *  #580 — `opts.canadaScope` opts the operator queue into the server-side Canada scope
 *  (the CAP list is always update-chain-deduped server-side; the map fetch omits scope). */
export async function fetchExtremeWeatherWarnings(
  bbox: BboxQuery,
  signal: AbortSignal,
  since?: string,
  opts: { canadaScope?: boolean } = {},
): Promise<CapAlertFeatureCollection> {
  const q = anomalyListQuery(bbox, since) + (opts.canadaScope ? '&canada_scope=1' : '');
  const res = await fetch(`/api/cap-alerts/extreme?${q}`, { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`fetchExtremeWeatherWarnings failed ${res.status}`);
  return (await res.json()) as CapAlertFeatureCollection;
}

export async function fetchCapAlertDetail(
  id: string,
  signal?: AbortSignal,
): Promise<CapAlertDetail> {
  const res = await fetch(`/api/cap-alerts/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchCapAlertDetail failed ${res.status}`);
  return (await res.json()) as CapAlertDetail;
}

export async function fetchAircraft(
  bbox: BboxQuery,
  signal: AbortSignal,
): Promise<AircraftFeatureCollection> {
  // include_stale keeps an aircraft whose transponder has gone quiet on the map
  // rather than dropping the marker: tracking_status==='stale' renders faded at
  // its last known position, and past 2h it becomes the ambient 'based' tier
  // ("last seen here / likely based nearby", #245) that persists until the next
  // live fix. No max_stale_minutes: the server's 30-day basing retention cap
  // bounds how long a permanently-quiet airframe stays on the COP.
  const params = new URLSearchParams({
    bbox: bboxParam(bbox),
    limit: '2000',
    include_stale: 'true',
  });
  const res = await fetch(`/api/aircraft?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchAircraft failed ${res.status}`);
  return (await res.json()) as AircraftFeatureCollection;
}

export async function fetchAircraftDetail(
  id: string,
  signal?: AbortSignal,
): Promise<AircraftDetail> {
  const res = await fetch(`/api/aircraft/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchAircraftDetail failed ${res.status}`);
  return (await res.json()) as AircraftDetail;
}

/* ------------------------------------------------------------------ *
 * L.2.2 — Aircraft & Marine render (R.1d vessels). Web-local contracts:
 * the /api/vessels route has no shared-types module yet.
 * ------------------------------------------------------------------ */

export interface VesselProperties {
  id: string;
  mmsi: string | null;
  vessel_name: string | null;
  /** icebreaker | hero_patrol | cape_lifeboat | bay_lifeboat | catamaran_patrol |
   *  rhib | hovercraft | science | navaids | offshore_patrol. */
  vessel_type: string | null;
  ais_class: string | null;
  /** reliable | intermittent | discover_live | none_static | dark_risk. */
  ais_visibility: string | null;
  position_source: string | null;
  status: string;
  speed_kt: number | null;
  heading_deg: number | null;
  nav_status: string | null;
  homeport: string | null;
  primary_waters: string | null;
  last_seen_at: string | null;
}
export interface VesselFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: VesselProperties;
}
export interface VesselFeatureCollection {
  type: 'FeatureCollection';
  features: VesselFeature[];
}
export interface VesselDetail extends Omit<VesselFeature, 'geometry'> {
  geometry: { type: 'Point'; coordinates: [number, number] } | null;
  properties: VesselProperties & {
    imo: string | null;
    call_sign: string | null;
    source_url: string | null;
    operator_asset_name: string | null;
    metadata: Record<string, unknown> | null;
  };
}

export async function fetchVessels(
  bbox: BboxQuery,
  signal: AbortSignal,
): Promise<VesselFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox), limit: '2000' });
  const res = await fetch(`/api/vessels?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchVessels failed ${res.status}`);
  return (await res.json()) as VesselFeatureCollection;
}

export async function fetchVesselDetail(mmsi: string, signal?: AbortSignal): Promise<VesselDetail> {
  const res = await fetch(`/api/vessels/${mmsi}`, { signal });
  if (!res.ok) throw new Error(`fetchVesselDetail failed ${res.status}`);
  return (await res.json()) as VesselDetail;
}

/* ------------------------------------------------------------------ *
 * Unified Anomaly Queue working-state (migration 0058, design-council Part II/VIII
 * → #140). Persists ✓ Ack + micro-status + owner for ALL FIVE queue sources, so the
 * operator's working state survives reload + is visible to the next shift. Replaces
 * the client-side in-memory Set/Map + window.prompt owner path.
 * ------------------------------------------------------------------ */

export type MicroStatus = 'new' | 'working' | 'resolving';
/** #331 reversible per-anomaly assessment axis (migration 0146) — the operator's
 *  reversible triage disposition, DISTINCT from ✓ Ack and micro-status. */
export type AnomalyAssessment = 'unconfirmed' | 'confirmed' | 'false_positive';
/** Matches the server source_type enum + the client QueueSource. */
export type AnomalyWorkstateSource =
  | 'hotspot'
  | 'cap'
  | 'smoke'
  | 'ice_storm'
  | 'flood'
  | 'media_signal'
  | 'oc_fire';

export interface AnomalyWorkstatePatch {
  acknowledged?: boolean;
  micro_status?: MicroStatus;
  /** null clears the owner chip; omit to leave unchanged. */
  owner_label?: string | null;
  /** #331 reversible assessment axis — omit to leave unchanged. Reversible both ways;
   *  each transition is audited append-only server-side and NEVER raises a dispatch rec. */
  assessment?: AnomalyAssessment;
  /** Optional false-positive note; omit to leave unchanged. */
  assessment_reason?: string | null;
  /** First-acknowledge-wins claim (#264): the write lands only while the row is
   *  unacknowledged; a lost claim is a graceful 200 no-op whose response carries
   *  the winning owner. Requires acknowledged: true. */
  claim?: boolean;
}
export interface AnomalyWorkstate {
  source_type: AnomalyWorkstateSource;
  source_id: string;
  work_acknowledged_at: string | null;
  micro_status: MicroStatus;
  /** #331 reversible assessment axis (migration 0146). */
  assessment: AnomalyAssessment;
  assessment_at: string | null;
  owner_label: string | null;
  /** Claim-mode responses only (#264): did THIS request win the claim? */
  claimed?: boolean;
}

/** Per-source counts for one bbox (#146). The panel fetches this for the national
 *  Canada envelope and subtracts the in-view counts to drive the "▲ N outside view"
 *  decoupling chip — no geometry, just scalars, so it's cheap per refresh. */
export interface AnomalyCounts {
  hotspot: number;
  cap: number;
  smoke: number;
  ice_storm: number;
  flood: number;
  media_signal: number;
  /** #444 — OC wildfires whose risk-gated Response Level reaches the urgent band (RL≥3). */
  oc_fire: number;
  total: number;
}

export interface AnomalyCountsOpts {
  minConfidence?: number;
  since?: string;
  /** #580 — opt into the server-side Canada scope + OC-fire freshness so the national
   *  badge numbers are Canada-scoped and never count an implausibly-stale fixture fire. */
  canadaScope?: boolean;
  freshOnly?: boolean;
}

export async function fetchAnomalyCounts(
  bbox: BboxQuery,
  signal: AbortSignal,
  opts: AnomalyCountsOpts = {},
): Promise<AnomalyCounts> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox) });
  if (opts.minConfidence != null) params.set('min_confidence', String(opts.minConfidence));
  if (opts.since) params.set('since', opts.since);
  if (opts.canadaScope) params.set('canada_scope', '1');
  if (opts.freshOnly) params.set('fresh_only', '1');
  const res = await fetch(`/api/anomaly-counts?${params.toString()}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`fetchAnomalyCounts failed ${res.status}`);
  return (await res.json()) as AnomalyCounts;
}

/** Persist a partial update to a queue row's working state. */
export async function putAnomalyWorkstate(
  sourceType: AnomalyWorkstateSource,
  sourceId: string,
  patch: AnomalyWorkstatePatch,
  sessionId: string,
  signal?: AbortSignal,
): Promise<AnomalyWorkstate> {
  const res = await fetch(`/api/anomaly-workstate/${sourceType}/${sourceId}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...patch, session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(`putAnomalyWorkstate failed ${res.status}`);
  return (await res.json()) as AnomalyWorkstate;
}

/* ------------------------------------------------------------------ *
 * L.2.2 — smoke_detected anomalies (§6.2). Web-local contracts.
 * ------------------------------------------------------------------ */

export interface SmokeAnomalyProperties {
  id: string;
  kind: 'smoke_detected';
  status: 'pending' | 'confirmed' | 'dismissed';
  created_at: string;
  detection_id: string;
  camera_id: string;
  camera_name: string;
  camera_source: string | null;
  confidence: number | null;
  detected_at: string;
  ptz_azimuth_deg: number | null;
  model: string | null;
  confirmed_as_hotspot_id: string | null;
  dismissed_reason: string | null;
  // Unified Anomaly Queue working-state (migration 0058, #140).
  work_acknowledged_at?: string | null;
  micro_status?: MicroStatus;
  /** #331 reversible assessment axis (migration 0146). */
  assessment?: AnomalyAssessment;
  owner_label?: string | null;
}
export interface SmokeAnomalyFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: SmokeAnomalyProperties;
}
export interface SmokeAnomalyFeatureCollection {
  type: 'FeatureCollection';
  features: SmokeAnomalyFeature[];
}
export interface SmokeAnomalyDetail extends SmokeAnomalyFeature {
  properties: SmokeAnomalyProperties & { bbox: unknown };
}

export async function fetchSmokeDetectionAnomalies(
  bbox: BboxQuery,
  signal: AbortSignal,
  since?: string,
): Promise<SmokeAnomalyFeatureCollection> {
  const res = await fetch(`/api/smoke-detection-anomalies?${anomalyListQuery(bbox, since)}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`fetchSmokeDetectionAnomalies failed ${res.status}`);
  return (await res.json()) as SmokeAnomalyFeatureCollection;
}

export async function fetchSmokeAnomalyDetail(
  id: string,
  signal?: AbortSignal,
): Promise<SmokeAnomalyDetail> {
  const res = await fetch(`/api/smoke-detection-anomalies/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchSmokeAnomalyDetail failed ${res.status}`);
  return (await res.json()) as SmokeAnomalyDetail;
}

/** Confirm Smoke → promotes to a high_risk_hotspot. Returns the new hotspot id. */
export async function confirmSmokeAnomaly(
  id: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<{ status: string; hotspot_id: string }> {
  const res = await fetch(`/api/smoke-detection-anomalies/${id}/confirm`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(`confirmSmokeAnomaly failed ${res.status}`);
  return (await res.json()) as { status: string; hotspot_id: string };
}

/** Dismiss Smoke → false positive; auto-leaves the queue. */
export async function dismissSmokeAnomaly(
  id: string,
  sessionId: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<{ status: string; id: string }> {
  const res = await fetch(`/api/smoke-detection-anomalies/${id}/dismiss`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, reason }),
    signal,
  });
  if (!res.ok) throw new Error(`dismissSmokeAnomaly failed ${res.status}`);
  return (await res.json()) as { status: string; id: string };
}

/* ------------------------------------------------------------------ *
 * Ice Storm Fusion Anomaly (§4.5 / §3B.8, IMS §7.2). Web-local contracts.
 * The act-register anomaly on top of the ambient Power Outages layer: a
 * clustered-outage + freezing-rain/ice-storm-warning signal, SCORED with an IMS
 * Response Level. Acknowledge (demote) or Dismiss (false positive); auto-resolves
 * when the cluster dissipates or the warning expires.
 * ------------------------------------------------------------------ */

export interface IceStormAnomalyProperties {
  id: string;
  kind: 'ice_storm';
  status: 'active' | 'acknowledged' | 'resolved' | 'dismissed';
  anomaly_type: 'anticipatory' | 'impact';
  response_level: number;
  outage_count: number;
  customers_affected: number | null;
  utilities: string[];
  radius_m: number;
  cap_alert_id: string | null;
  cap_event: string | null;
  cap_severity: string | null;
  cap_expires_at: string | null;
  first_detected_at: string;
  last_seen_at: string;
  created_at: string;
  /** Last in-place RL-escalation stamp (#281); null = never escalated since first seen. */
  escalated_at: string | null;
  // Unified Anomaly Queue working-state (migration 0058, #140).
  work_acknowledged_at?: string | null;
  micro_status?: MicroStatus;
  /** #331 reversible assessment axis (migration 0146). */
  assessment?: AnomalyAssessment;
  owner_label?: string | null;
  // ── #445-ws2 — the owning incident's OPERATOR-ASSESSED Response Level overlay (migration 0148) ──
  /** The IC's operator-assessed Response Level on the owning (non-closed) incident, or null when
   *  not owned / no pin. The map disc + queue row read the ESCALATION-SAFE max of this and
   *  `response_level` (escalationSafeResponseLevel): an IC escalation lifts them; an IC
   *  de-escalation can never bury a worse derived. The derived `response_level` is NEVER mutated. */
  operator_response_level?: number | null;
  /** The assessing Incident Commander (command_officer_name snapshot) — the "assessed by <IC>"
   *  honesty annotation on the anomaly detail. null when no pin. */
  operator_rl_by?: string | null;
  /** The IC's assessment reason for the pin, or null. */
  operator_rl_reason?: string | null;
  /** When the IC's assessment landed (ISO), or null. */
  operator_rl_at?: string | null;
  /** Ownership-coordination — the cross-agency "who holds command" ref (migration 0151 view). */
  owned_incident?: OwnedIncidentRef | null;
  /** The owning incident's lifecycle phase; null when this anomaly is not a tracked incident. */
  incident_lifecycle_state?: IncidentLifecycleState | null;
}
export interface IceStormAnomalyFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: IceStormAnomalyProperties;
}
export interface IceStormAnomalyFeatureCollection {
  type: 'FeatureCollection';
  features: IceStormAnomalyFeature[];
}
export interface IceCommunityInReach {
  id: string;
  name: string;
  community_type: string;
  population: number | null;
  vulnerable_population: number | null;
  distance_km: number;
}
export interface IceStormAnomalyDetail extends IceStormAnomalyFeature {
  properties: IceStormAnomalyProperties & {
    corroborating_signals: unknown;
    communities_in_reach: IceCommunityInReach[];
  };
}

export async function fetchIceStormAnomalies(
  bbox: BboxQuery,
  signal: AbortSignal,
  since?: string,
): Promise<IceStormAnomalyFeatureCollection> {
  const res = await fetch(`/api/ice-storm-anomalies?${anomalyListQuery(bbox, since)}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`fetchIceStormAnomalies failed ${res.status}`);
  return (await res.json()) as IceStormAnomalyFeatureCollection;
}

export async function fetchIceStormAnomalyDetail(
  id: string,
  signal?: AbortSignal,
): Promise<IceStormAnomalyDetail> {
  const res = await fetch(`/api/ice-storm-anomalies/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchIceStormAnomalyDetail failed ${res.status}`);
  return (await res.json()) as IceStormAnomalyDetail;
}

/** Acknowledge → demotes in the queue; marker stays. */
export async function acknowledgeIceStormAnomaly(
  id: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<{ status: string; id: string }> {
  const res = await fetch(`/api/ice-storm-anomalies/${id}/acknowledge`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(`acknowledgeIceStormAnomaly failed ${res.status}`);
  return (await res.json()) as { status: string; id: string };
}

/** Dismiss → false positive; auto-leaves the queue. */
export async function dismissIceStormAnomaly(
  id: string,
  sessionId: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<{ status: string; id: string }> {
  const res = await fetch(`/api/ice-storm-anomalies/${id}/dismiss`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, reason }),
    signal,
  });
  if (!res.ok) throw new Error(`dismissIceStormAnomaly failed ${res.status}`);
  return (await res.json()) as { status: string; id: string };
}

/* ------------------------------------------------------------------ *
 * Flood Fusion Anomaly (E.F.2, §2.7 / §4.4). The act-register signal on top of the
 * ambient Hydrometric Gauges layer — a gauge sustained rate-of-rise + community-in-
 * reach signal the operator acknowledges or dismisses.
 * ------------------------------------------------------------------ */

export interface FloodAnomalyProperties {
  id: string;
  kind: 'flood';
  status: 'active' | 'acknowledged' | 'resolved' | 'dismissed';
  anomaly_type: 'watch' | 'warning';
  response_level: number;
  station_number: string;
  station_name: string | null;
  river: string | null;
  water_level_m: number | null;
  discharge_cms: number | null;
  rate_of_rise_cm_per_hr: number;
  gauge_sparse: boolean;
  communities_in_reach: number;
  max_population: number | null;
  total_vulnerable: number | null;
  cap_alert_id: string | null;
  cap_event: string | null;
  cap_severity: string | null;
  cap_expires_at: string | null;
  first_detected_at: string;
  last_seen_at: string;
  created_at: string;
  /** Last in-place RL-escalation stamp (#281); null = never escalated since first seen. */
  escalated_at: string | null;
  // Unified Anomaly Queue working-state (migration 0058, #140).
  work_acknowledged_at?: string | null;
  micro_status?: MicroStatus;
  /** #331 reversible assessment axis (migration 0146). */
  assessment?: AnomalyAssessment;
  owner_label?: string | null;
  // ── #445-ws2 — the owning incident's OPERATOR-ASSESSED Response Level overlay (migration 0148) ──
  /** The IC's operator-assessed Response Level on the owning (non-closed) incident, or null when
   *  not owned / no pin. The map disc + queue row read the ESCALATION-SAFE max of this and
   *  `response_level` (escalationSafeResponseLevel): an IC escalation lifts them; an IC
   *  de-escalation can never bury a worse derived. The derived `response_level` is NEVER mutated. */
  operator_response_level?: number | null;
  /** The assessing Incident Commander (command_officer_name snapshot) — the "assessed by <IC>"
   *  honesty annotation on the anomaly detail. null when no pin. */
  operator_rl_by?: string | null;
  /** The IC's assessment reason for the pin, or null. */
  operator_rl_reason?: string | null;
  /** When the IC's assessment landed (ISO), or null. */
  operator_rl_at?: string | null;
  /** Ownership-coordination — the cross-agency "who holds command" ref (migration 0151 view). */
  owned_incident?: OwnedIncidentRef | null;
  /** The owning incident's lifecycle phase; null when this anomaly is not a tracked incident. */
  incident_lifecycle_state?: IncidentLifecycleState | null;
}
export interface FloodAnomalyFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: FloodAnomalyProperties;
}
export interface FloodAnomalyFeatureCollection {
  type: 'FeatureCollection';
  features: FloodAnomalyFeature[];
}
export interface FloodCommunityInReach {
  id: string;
  name: string;
  community_type: string;
  population: number | null;
  vulnerable_population: number | null;
  distance_km: number;
  lon: number;
  lat: number;
}
export interface FloodGaugeReading {
  observed_at: string;
  water_level_m: number | null;
  discharge_cms: number | null;
  rate_of_rise_cm_per_hr: number | null;
}
export interface FloodAnomalyDetail extends FloodAnomalyFeature {
  properties: FloodAnomalyProperties & {
    corroborating_signals: unknown;
    communities_in_reach_detail: FloodCommunityInReach[];
    gauge_readings: FloodGaugeReading[];
    evacuation_incident_id: string | null;
    evacuation_incident_number: string | null;
  };
}

export async function fetchFloodAnomalies(
  bbox: BboxQuery,
  signal: AbortSignal,
  since?: string,
): Promise<FloodAnomalyFeatureCollection> {
  const res = await fetch(`/api/flood-anomalies?${anomalyListQuery(bbox, since)}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`fetchFloodAnomalies failed ${res.status}`);
  return (await res.json()) as FloodAnomalyFeatureCollection;
}

export async function fetchFloodAnomalyDetail(
  id: string,
  signal?: AbortSignal,
): Promise<FloodAnomalyDetail> {
  const res = await fetch(`/api/flood-anomalies/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchFloodAnomalyDetail failed ${res.status}`);
  return (await res.json()) as FloodAnomalyDetail;
}

/** Acknowledge → demotes in the queue; marker stays. */
export async function acknowledgeFloodAnomaly(
  id: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<{ status: string; id: string }> {
  const res = await fetch(`/api/flood-anomalies/${id}/acknowledge`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(`acknowledgeFloodAnomaly failed ${res.status}`);
  return (await res.json()) as { status: string; id: string };
}

/** Dismiss → false positive; auto-leaves the queue. */
export async function dismissFloodAnomaly(
  id: string,
  sessionId: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<{ status: string; id: string }> {
  const res = await fetch(`/api/flood-anomalies/${id}/dismiss`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, reason }),
    signal,
  });
  if (!res.ok) throw new Error(`dismissFloodAnomaly failed ${res.status}`);
  return (await res.json()) as { status: string; id: string };
}

/* ------------------------------------------------------------------ *
 * #184 — media_signal igniter anomalies (migration 0072). Web-local contracts.
 * A LOW-CONFIDENCE "needs assessment" chatter spike raised by the media-signals
 * worker's igniter pass (geo-clustered wire signals, N+ distinct outlets, above
 * baseline, no official anomaly of the family in radius). UNSCORED — never
 * carries a Response Level. Confirm keeps it in the queue; Dismiss removes it.
 * The evidence payload IS the operator's assessment surface.
 * ------------------------------------------------------------------ */

export interface MediaSignalEvidenceSignal {
  id: string;
  source: string;
  /** Distinct-origin identity (e.g. 'gdelt:cbc.ca'). */
  origin: string;
  title: string | null;
  url: string | null;
  observed_at: string;
  geo_name: string | null;
}
export interface MediaSignalEvidence {
  hazard_key: string;
  signals: MediaSignalEvidenceSignal[];
  origins: string[];
  baseline_per_window: number;
  suppression: { probed_radius_m: number; official_anomaly_found: boolean };
  thresholds: {
    window_min: number;
    radius_m: number;
    min_signals: number;
    min_origins: number;
    baseline_days?: number;
    baseline_mult?: number;
  };
}
export interface MediaSignalAnomalyProperties {
  id: string;
  kind: 'media_signal_spike';
  status: 'active' | 'confirmed' | 'dismissed' | 'resolved';
  hazard_key: string;
  cluster_key: string;
  radius_m: number;
  signal_count: number;
  source_count: number;
  baseline_per_window: number;
  evidence: MediaSignalEvidence;
  dismissed_reason: string | null;
  first_detected_at: string;
  last_seen_at: string;
  created_at: string;
  // Unified Anomaly Queue working-state (migration 0058, #140).
  work_acknowledged_at?: string | null;
  micro_status?: MicroStatus;
  /** #331 reversible assessment axis (migration 0146). */
  assessment?: AnomalyAssessment;
  owner_label?: string | null;
}
export interface MediaSignalAnomalyFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: MediaSignalAnomalyProperties;
}
export interface MediaSignalAnomalyFeatureCollection {
  type: 'FeatureCollection';
  features: MediaSignalAnomalyFeature[];
}

export async function fetchMediaSignalAnomalies(
  bbox: BboxQuery,
  signal: AbortSignal,
  since?: string,
): Promise<MediaSignalAnomalyFeatureCollection> {
  const res = await fetch(`/api/media-signal-anomalies?${anomalyListQuery(bbox, since)}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`fetchMediaSignalAnomalies failed ${res.status}`);
  return (await res.json()) as MediaSignalAnomalyFeatureCollection;
}

/** Confirm — operator assessed the spike as real; stays in the queue. */
export async function confirmMediaSignalAnomaly(
  id: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<{ status: string; id: string }> {
  const res = await fetch(`/api/media-signal-anomalies/${id}/confirm`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(`confirmMediaSignalAnomaly failed ${res.status}`);
  return (await res.json()) as { status: string; id: string };
}

/** Dismiss — judged noise; auto-leaves the queue. */
export async function dismissMediaSignalAnomaly(
  id: string,
  sessionId: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<{ status: string; id: string }> {
  const res = await fetch(`/api/media-signal-anomalies/${id}/dismiss`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, reason }),
    signal,
  });
  if (!res.ok) throw new Error(`dismissMediaSignalAnomaly failed ${res.status}`);
  return (await res.json()) as { status: string; id: string };
}

/* ------------------------------------------------------------------ *
 * Hydrometric Gauges (E.F.1 render, Flood Risk group). The ambient WSC river-gauge
 * layer — latest reading per station, coloured by rate-of-rise band.
 * ------------------------------------------------------------------ */

export interface HydrometricGaugeServed {
  name: string;
  coverage: string;
}
export interface HydrometricGaugeProperties {
  station_number: string;
  station_name: string | null;
  river: string | null;
  water_level_m: number | null;
  discharge_cms: number | null;
  rate_of_rise_cm_per_hr: number | null;
  observed_at: string;
  gauge_sparse: boolean;
  serves: HydrometricGaugeServed[];
  threshold_label: string | null;
  attribution: string | null;
}
export interface HydrometricGaugeFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: HydrometricGaugeProperties;
}
export interface HydrometricGaugeFeatureCollection {
  type: 'FeatureCollection';
  features: HydrometricGaugeFeature[];
}

export async function fetchHydrometricGauges(
  bbox: BboxQuery,
  signal: AbortSignal,
): Promise<HydrometricGaugeFeatureCollection> {
  const res = await fetch(`/api/hydrometric-gauges?bbox=${bboxParam(bbox)}`, { signal });
  if (!res.ok) throw new Error(`fetchHydrometricGauges failed ${res.status}`);
  return (await res.json()) as HydrometricGaugeFeatureCollection;
}

/* ------------------------------------------------------------------ *
 * L.2.2 — Drone Operator Coverage (§3B.6). Web-local contracts.
 * ------------------------------------------------------------------ */

/**
 * UNIFIED with the marketplace fleet (Wave-1 drone unify #343): a coverage feature IS a
 * `drone_assets` row — its `id` is the `drone_asset_id` the matcher returns — so every
 * coverage pin is taskable. The server (drone-coverage.ts) reads the same drone_assets +
 * drone_asset_sensors rows the matcher ranks, JOINed to agencies for the operator name +
 * the `government_owned` $0 marker.
 */
export interface DroneCoverageProperties {
  id: string;
  kind: 'drone_coverage';
  /** Asset name (e.g. "MNRF UAV — Sioux Lookout"). */
  name: string;
  operator_agency_id: string;
  /** Operator org display name (from agencies) — fixes the old UUID-only pin name (F10/#339). */
  operator_name: string | null;
  /** Government / public-service operator ⇒ $0-rated ("no charge"); else commercial / priced. */
  government_owned: boolean;
  /** Asset availability_status (raw) — available / maintenance / unavailable. */
  status: string;
  /** Phase 4c — the §3B.6 readiness band: 'committed' when the asset has an active mission,
   *  else its availability_status. Drives the readiness badge. REQUIRED here (the coverage route
   *  always populates it and the sole fixture is updated in lockstep) — deliberately stricter than
   *  the sibling DroneMatchCandidate, where these same 0103 fields are OPTIONAL to spare the many
   *  existing candidate fixtures (the `government_owned?` precedent). Coverage has one constructor;
   *  the matcher has many — hence the asymmetry. */
  readiness: DroneReadiness;
  illustrative: boolean;
  platform_category: DronePlatformCategory | null;
  downlink_type: DroneDownlinkType | null;
  /** Combined reach (km) — the coverage-circle radius + the reachability test. */
  service_radius_km: number | null;
  /** Phase 4c response-time telemetry (drone_assets cols, migration 0103) — the §3B.6
   *  four-field block + the client-derived ETA-to-on-station. null when the asset has no
   *  response-time profile. */
  cruise_speed_kmh: number | null;
  launch_delay_minutes: number | null;
  flight_endurance_min: number | null;
  area_coverage_sqkm: number | null;
  sensors: DroneSensorType[];
}
export interface DroneCoverageFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: DroneCoverageProperties;
}
export interface DroneCoverageFeatureCollection {
  type: 'FeatureCollection';
  features: DroneCoverageFeature[];
}
export interface DroneCoverageDetail extends DroneCoverageFeature {
  properties: DroneCoverageProperties & { tail_number: string | null };
}

export async function fetchDroneCoverage(
  bbox: BboxQuery,
  signal: AbortSignal,
): Promise<DroneCoverageFeatureCollection> {
  const res = await fetch(`/api/drone-coverage?bbox=${bboxParam(bbox)}`, { signal });
  if (!res.ok) throw new Error(`fetchDroneCoverage failed ${res.status}`);
  return (await res.json()) as DroneCoverageFeatureCollection;
}

export async function fetchDroneCoverageDetail(
  id: string,
  signal?: AbortSignal,
): Promise<DroneCoverageDetail> {
  const res = await fetch(`/api/drone-coverage/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchDroneCoverageDetail failed ${res.status}`);
  return (await res.json()) as DroneCoverageDetail;
}

/**
 * Drone D1 #320 — "request live eyes" matching. STATELESS: returns the fit-ranked
 * `drone_assets` (#317) that can reach the AO and cover the required sensors. Persists
 * nothing (no mission); a gov-facing COP read (authedFetch so the session rides under
 * enforcement). Mission creation + dispatch from a chosen option is #321.
 */
export async function matchDrones(
  body: DroneMatchRequestInput,
  signal?: AbortSignal,
): Promise<DroneMatchResponse> {
  const res = await authedFetch('/api/drone-match', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`matchDrones failed ${res.status}`);
  return (await res.json()) as DroneMatchResponse;
}

/* ------------------------------------------------------------------ *
 * Drone D1 #319 — mission lifecycle. Two surfaces over the #317 drone_missions record:
 *   - GOV / requestor (COP routes): create from a #320 pick, list, cancel / bill / pay.
 *   - OPERATOR (portal routes, behind the #316 class gate): assigned list, accept /
 *     decline / flown. The SERVER is the isolation + actor-guard wall; the web is a thin
 *     client. Operators never see the requestor-scoped billing ledger.
 * ------------------------------------------------------------------ */

export interface DroneMissionLedgerView {
  status: string;
  billable: boolean;
  operator_kind: string;
  /** The recorded per-use fee (CAD) — the operator's quoted price flowed in at flown. */
  per_use_fee_cad: number | null;
}

/** The #321 dispatch terms an operator stamps on accept (present once status ≥ accepted). */
export interface MissionDispatchTerms {
  eta_minutes: number | null;
  quoted_price_cad: number | null;
  /** On-station window (#345): requested by the gov side at create; offered (committed) by the
   *  operator at accept-with-terms. Feed the price estimate + the drones-on-incident view. */
  requested_on_station_minutes: number | null;
  offered_on_station_minutes: number | null;
  feed_url: string | null;
  feed_type: string | null;
  confirmed_at: string | null;
  enroute_at: string | null;
}

/** A mission as the gov/requestor sees it (includes the ledger summary + dispatch terms). */
export interface DroneMissionView extends MissionDispatchTerms {
  id: string;
  status: MissionStatus;
  requestor_agency_id: string;
  operator_agency_id: string | null;
  operator_name: string | null;
  drone_asset_id: string | null;
  asset_name: string | null;
  incident_id: string | null;
  /** #388 — the linked incident's name + centroid for the detail-panel back-ref +
   *  fly-to. Null when the mission isn't linked to an incident (or it was deleted). */
  incident_name: string | null;
  incident_lat: number | null;
  incident_lon: number | null;
  requested_capabilities: Record<string, unknown>;
  billable: boolean;
  requested_at: string | null;
  accepted_at: string | null;
  flown_at: string | null;
  cancelled_at: string | null;
  declined_reason: string | null;
  created_at: string;
  updated_at: string;
  /** Phase 4d — explicit requestor RELEASE: the demobilize flag + optional reason
   *  (null until released). When set, the asset frees (re-taskable) and the band reads
   *  demobilized regardless of marketplace status. */
  demobilized_at: string | null;
  demobilization_reason: string | null;
  /** Phase 4d — the derived IMS resource-lifecycle band (so a tasked drone reads in the
   *  same language as every other resource). Null = no resource line (declined/cancelled). */
  lifecycle_band: ResourceLifecycleState | null;
  ledger: DroneMissionLedgerView | null;
  /** #346 — unread operator-authored messages on this mission (gov POV). */
  unread_count: number;
  /** #346 — the assigned operator's primary-pilot call-back phone. Present ONLY on the
   *  single-mission detail (fetchDroneMission); null when unpublished or no operator yet. */
  operator_contact_phone?: string | null;
}

/** A mission as the assigned operator sees it (NO billing ledger — requestor-scoped). */
export interface OperatorMissionView extends MissionDispatchTerms {
  id: string;
  status: MissionStatus;
  requestor_agency_id: string;
  requestor_name: string | null;
  operator_agency_id: string | null;
  drone_asset_id: string | null;
  asset_name: string | null;
  incident_id: string | null;
  /** Phase 4e — the incident context + requested urgency surfaced so the operator triages
   *  accept/decline IN the COP (§I.2 ☎4). The incident_* fields are null when the request
   *  isn't incident-linked; requested_urgency defaults to 'routine'. */
  incident_name: string | null;
  incident_number: string | null;
  hazard_type: string | null;
  incident_lat: number | null;
  incident_lon: number | null;
  requested_urgency: DroneRequestUrgency;
  requested_capabilities: Record<string, unknown>;
  requested_at: string | null;
  accepted_at: string | null;
  flown_at: string | null;
  declined_reason: string | null;
  created_at: string;
  updated_at: string;
  /** #346 — unread requestor-authored messages on this mission (operator POV). */
  unread_count: number;
}

export interface CreateDroneMissionInput {
  operator_agency_id: string;
  drone_asset_id?: string | null;
  incident_id?: string | null;
  ao?: { lat: number; lon: number } | null;
  required_sensors?: DroneSensorType[];
  platform_category?: DronePlatformCategory | null;
  /** Eyes-on window the requestor wants (minutes, #345). */
  requested_on_station_minutes?: number | null;
  /** Requested urgency (Phase 4e) — routine | urgent | immediate; defaults to routine. */
  requested_urgency?: DroneRequestUrgency;
}

/** The #321 accept-with-terms payload the operator submits to accept a mission. */
export interface AcceptMissionTermsInput {
  eta_minutes: number;
  quoted_price_cad: number;
  feed_url: string;
  feed_type: DroneFeedType;
  /** The sustainable on-station window the operator commits to (minutes, #345). */
  offered_on_station_minutes?: number | null;
}

/* --- gov / requestor side (COP routes) --- */

export async function createDroneMission(
  input: CreateDroneMissionInput,
): Promise<DroneMissionView> {
  const data = await portalJson<{ mission: DroneMissionView }>(
    await portalPost('/api/drone-missions', input),
    'createDroneMission',
  );
  return data.mission;
}

export async function fetchDroneMissions(
  signal?: AbortSignal,
  opts?: { incidentId?: string | null },
): Promise<DroneMissionView[]> {
  // Optional incident filter (#345) — the aggregated "drones on this incident" view.
  const qs = opts?.incidentId ? `?incident_id=${encodeURIComponent(opts.incidentId)}` : '';
  const res = await authedFetch(`/api/drone-missions${qs}`, { signal, cache: 'no-store' });
  if (res.status === 401 || res.status === 403) return [];
  const data = await portalJson<{ missions?: DroneMissionView[] }>(res, 'fetchDroneMissions');
  return data.missions ?? [];
}

async function govMissionAction(
  id: string,
  action: 'confirm' | 'cancel' | 'bill' | 'pay',
): Promise<DroneMissionView> {
  const data = await portalJson<{ mission: DroneMissionView }>(
    await portalPost(`/api/drone-missions/${id}/${action}`, {}),
    `${action}DroneMission`,
  );
  return data.mission;
}
/** #321: the requestor confirms the operator's accept-terms (accepted → confirmed). */
export const confirmDroneMission = (id: string): Promise<DroneMissionView> =>
  govMissionAction(id, 'confirm');
export const cancelDroneMission = (id: string): Promise<DroneMissionView> =>
  govMissionAction(id, 'cancel');
export const billDroneMission = (id: string): Promise<DroneMissionView> =>
  govMissionAction(id, 'bill');
export const payDroneMission = (id: string): Promise<DroneMissionView> =>
  govMissionAction(id, 'pay');
/** Phase 4d — the requestor/Logistics RELEASE (demobilize) of a tasked drone: frees the asset
 *  and flips the IMS band to demobilized. A PATCH (the release is a flag, not a status edge),
 *  mirroring PATCH /api/resource-requests/:id/demobilize. */
export async function demobilizeDroneMission(
  id: string,
  reason?: string,
): Promise<DroneMissionView> {
  const body = reason && reason.trim() ? { demobilization_reason: reason.trim() } : {};
  const data = await portalJson<{ mission: DroneMissionView }>(
    await portalPatch(`/api/drone-missions/${id}/demobilize`, body),
    'demobilizeDroneMission',
  );
  return data.mission;
}

/* ------------------------------------------------------------------ *
 * OUTBOUND agency tasks (#379, Coordination Re-Arch Phase 3b-2a) — the
 * requester (PEOC) surface: the agency picker, issue/list/get/cancel. The
 * AgencyView / AgencyTaskView shapes are the @tsr/shared-types source of truth.
 * ------------------------------------------------------------------ */

/** The agencies the operator can task DOWN — the AgencyPicker registry. The server excludes
 *  the caller's own agency (so only NON-SELF targets are offered). `opts.kinds` scopes the
 *  list to specific agency kinds via `?kind=` (W4d/#519 — the down-task picker passes
 *  DOWN_TASK_TARGET_KINDS to drop drone-operator orgs); omitted ⇒ every non-self agency
 *  (the IC lead-organization picker's unfiltered list). */
export async function fetchAgencies(
  signal?: AbortSignal,
  opts?: { kinds?: readonly string[] },
): Promise<AgencyView[]> {
  const qs =
    opts?.kinds && opts.kinds.length > 0 ? `?kind=${encodeURIComponent(opts.kinds.join(','))}` : '';
  const res = await authedFetch(`/api/agencies${qs}`, { signal, cache: 'no-store' });
  if (res.status === 401 || res.status === 403) return [];
  const data = await portalJson<{ agencies?: AgencyView[] }>(res, 'fetchAgencies');
  return data.agencies ?? [];
}

export interface IssueAgencyTaskInput {
  target_agency_id: string;
  narrative: string;
  urgency?: 'routine' | 'urgent' | 'immediate';
  response_level?: number | null;
  assistance_request_id?: string | null;
  incident_id?: string | null;
  resource_request_id?: string | null;
  dispatch_recommendation_id?: string | null;
  session_id?: string;
}

/** Issue an outbound agency task to a NON-SELF agency (the requester side). */
export async function issueAgencyTask(input: IssueAgencyTaskInput): Promise<AgencyTaskView> {
  const data = await portalJson<{ task: AgencyTaskView }>(
    await portalPost('/api/agency-tasks', input),
    'issueAgencyTask',
  );
  return data.task;
}

/** The agency tasks THIS agency issued (requester-scoped on the server). */
export async function fetchAgencyTasks(signal?: AbortSignal): Promise<AgencyTaskView[]> {
  const res = await authedFetch('/api/agency-tasks', { signal, cache: 'no-store' });
  if (res.status === 401 || res.status === 403) return [];
  const data = await portalJson<{ tasks?: AgencyTaskView[] }>(res, 'fetchAgencyTasks');
  return data.tasks ?? [];
}

/** Cancel an issued task (requester-only; issued|acknowledged|accepted|in_progress → cancelled). */
export async function cancelAgencyTask(id: string): Promise<AgencyTaskView> {
  const data = await portalJson<{ task: AgencyTaskView }>(
    await portalPost(`/api/agency-tasks/${id}/cancel`, {}),
    'cancelAgencyTask',
  );
  return data.task;
}

/* ------------------------------------------------------------------ *
 * INBOUND agency tasks (#379, Coordination Re-Arch Phase 3b-2b) — the
 * TARGET (tasked) agency's surface: its inbox + the acknowledge / accept /
 * decline / start / complete response transitions that close the round-trip.
 * ------------------------------------------------------------------ */

/** The tasks addressed TO this agency (target-scoped on the server) — the inbox worklist. */
export async function fetchInboxAgencyTasks(signal?: AbortSignal): Promise<AgencyTaskView[]> {
  const res = await authedFetch('/api/agency-tasks/inbox', { signal, cache: 'no-store' });
  if (res.status === 401 || res.status === 403) return [];
  const data = await portalJson<{ tasks?: AgencyTaskView[] }>(res, 'fetchInboxAgencyTasks');
  return data.tasks ?? [];
}

/** Drive one TARGET-side transition (acknowledge/accept/decline/start/complete). The body
 *  carries the optional structured back-channel for decline (reason) / complete (notes). */
async function advanceInboxAgencyTask(
  id: string,
  action: 'acknowledge' | 'accept' | 'decline' | 'start' | 'complete',
  body: Record<string, string> = {},
): Promise<AgencyTaskView> {
  const data = await portalJson<{ task: AgencyTaskView }>(
    await portalPost(`/api/agency-tasks/${id}/${action}`, body),
    `${action}AgencyTask`,
  );
  return data.task;
}

/** issued → acknowledged (target acknowledges receipt). */
export const acknowledgeAgencyTask = (id: string): Promise<AgencyTaskView> =>
  advanceInboxAgencyTask(id, 'acknowledge');
/** {issued,acknowledged} → accepted (target accepts the task). */
export const acceptAgencyTask = (id: string): Promise<AgencyTaskView> =>
  advanceInboxAgencyTask(id, 'accept');
/** {issued,acknowledged} → declined, with an optional reason the requester will see. */
export const declineAgencyTask = (id: string, reason?: string): Promise<AgencyTaskView> =>
  advanceInboxAgencyTask(id, 'decline', reason ? { declined_reason: reason } : {});
/** accepted → in_progress (target starts work). */
export const startAgencyTask = (id: string): Promise<AgencyTaskView> =>
  advanceInboxAgencyTask(id, 'start');
/** in_progress → completed, with an optional completion note the requester will see. */
export const completeAgencyTask = (id: string, notes?: string): Promise<AgencyTaskView> =>
  advanceInboxAgencyTask(id, 'complete', notes ? { completion_notes: notes } : {});

/* ------------------------------------------------------------------ *
 * Agency-task comms (#379, Coordination Re-Arch Phase 3b-2c) — the two-party CHAT
 * THREAD on an agency_task. BOTH the requester (PEOC) and the target (tasked agency)
 * read the SAME thread; each posts only as itself (the server stamps the author from
 * the verified identity). The API returns the MISSION wire shape, so the shipped
 * MissionMessageView / MessageThread UI is reused unchanged.
 * ------------------------------------------------------------------ */

/** The full thread for a task the caller is a party to (oldest first). */
export async function fetchAgencyTaskMessages(
  id: string,
  signal?: AbortSignal,
): Promise<MissionMessageView[]> {
  const res = await authedFetch(`/api/agency-tasks/${id}/messages`, { signal, cache: 'no-store' });
  if (res.status === 401 || res.status === 403 || res.status === 404) return [];
  const data = await portalJson<{ messages?: MissionMessageView[] }>(
    res,
    'fetchAgencyTaskMessages',
  );
  return data.messages ?? [];
}

/** Post a message to a task's thread (author stamped server-side from the verified party). */
export async function postAgencyTaskMessage(
  id: string,
  content: string,
): Promise<MissionMessageView> {
  const data = await portalJson<{ message: MissionMessageView }>(
    await portalPost(`/api/agency-tasks/${id}/messages`, { content }),
    'postAgencyTaskMessage',
  );
  return data.message;
}

/** Mark a message read by the acting party (requester or target). */
export async function markAgencyTaskMessageRead(id: string, msgId: string): Promise<void> {
  await portalPatch(`/api/agency-tasks/${id}/messages/${msgId}/read`, {});
}

export interface TaskAgencyInput {
  target_agency_id: string;
  narrative?: string;
  urgency?: 'routine' | 'urgent' | 'immediate';
  response_level?: number | null;
  session_id?: string;
}

/** "Task agency" on a dispatch recommendation — issues a down-task to a NON-SELF agency
 *  (the new dominant control; the legacy forward-to-self stays available, secondary). */
export async function taskAgencyFromDispatch(
  recId: string,
  input: TaskAgencyInput & { resource_request_id?: string | null },
): Promise<{ agency_task_id: string }> {
  return portalJson<{ agency_task_id: string }>(
    await portalPost(`/api/dispatch-recommendations/${recId}/task-agency`, input),
    'taskAgencyFromDispatch',
  );
}

/** "Task agency" on an assistance request — issues a down-task to a NON-SELF agency
 *  carrying assistance_request_id (replaces the forward-to-self PEOC default). */
export async function taskAgencyFromAssistance(
  assistId: string,
  input: TaskAgencyInput,
): Promise<{ agency_task_id: string }> {
  return portalJson<{ agency_task_id: string }>(
    await portalPost(`/api/assistance-requests/${assistId}/task-agency`, input),
    'taskAgencyFromAssistance',
  );
}

/**
 * #322 feed-embed — fetch the COP feed descriptor for a mission (requestor-scoped on the server).
 * The server gates live-only (enroute/flown) + classifies the embed (hls proxy / youtube resolver
 * / honest unplayable), so the client never touches the operator's feed_url directly. Returns
 * null when the mission isn't this agency's (404/403) so the panel degrades to "no feed".
 */
export async function fetchMissionFeed(
  id: string,
  signal?: AbortSignal,
): Promise<MissionFeedDescriptor | null> {
  const res = await authedFetch(`/api/drone-missions/${id}/feed`, { signal, cache: 'no-store' });
  if (res.status === 401 || res.status === 403 || res.status === 404) return null;
  const data = await portalJson<{ feed?: MissionFeedDescriptor }>(res, 'fetchMissionFeed');
  return data.feed ?? null;
}

/* --- operator side (portal routes) --- */

export async function fetchOperatorMissions(signal?: AbortSignal): Promise<OperatorMissionView[]> {
  const res = await authedFetch('/api/operator-portal/missions', { signal, cache: 'no-store' });
  if (res.status === 401) return [];
  const data = await portalJson<{ missions?: OperatorMissionView[] }>(res, 'fetchOperatorMissions');
  return data.missions ?? [];
}

/** #321: the operator accepts WITH TERMS (eta + quoted price + feed link). */
export async function acceptOperatorMission(
  id: string,
  terms: AcceptMissionTermsInput,
): Promise<OperatorMissionView> {
  const data = await portalJson<{ mission: OperatorMissionView }>(
    await portalPost(`/api/operator-portal/missions/${id}/accept`, terms),
    'acceptOperatorMission',
  );
  return data.mission;
}

export async function declineOperatorMission(
  id: string,
  reason?: string,
): Promise<OperatorMissionView> {
  const data = await portalJson<{ mission: OperatorMissionView }>(
    await portalPost(`/api/operator-portal/missions/${id}/decline`, { reason: reason ?? null }),
    'declineOperatorMission',
  );
  return data.mission;
}

/** #321: the operator dispatches the asset (confirmed → enroute). */
export async function enrouteOperatorMission(id: string): Promise<OperatorMissionView> {
  const data = await portalJson<{ mission: OperatorMissionView }>(
    await portalPost(`/api/operator-portal/missions/${id}/enroute`, {}),
    'enrouteOperatorMission',
  );
  return data.mission;
}

export async function flyOperatorMission(id: string): Promise<OperatorMissionView> {
  const data = await portalJson<{ mission: OperatorMissionView }>(
    await portalPost(`/api/operator-portal/missions/${id}/flown`, {}),
    'flyOperatorMission',
  );
  return data.mission;
}

/* ------------------------------------------------------------------ *
 * Drone mission comms (Wave-2 Phase 1 #346) — gov ↔ pilot two-way messaging on a
 * mission. Both parties read the SAME thread; each posts only as itself (the server
 * stamps the author from the verified identity). The reusable MessageThread UI renders
 * a MissionMessageView[]; these fns are the gov (COP) + operator (portal) wirings.
 * ------------------------------------------------------------------ */

/** Which party authored / is viewing a message — drives alignment + read-receipt logic. */
export type ThreadParty = 'requestor' | 'operator';

export interface MissionMessageView {
  id: string;
  mission_id: string;
  author_agency_id: string;
  author_role: ThreadParty;
  content: string;
  message_type: string;
  read_by_requestor_at: string | null;
  read_by_operator_at: string | null;
  created_at: string;
}

/* --- gov / requestor side (COP routes) --- */

/** Single-mission detail (includes operator_contact_phone for the gov "Call pilot" link). */
export async function fetchDroneMission(
  id: string,
  signal?: AbortSignal,
): Promise<DroneMissionView | null> {
  const res = await authedFetch(`/api/drone-missions/${id}`, { signal, cache: 'no-store' });
  if (res.status === 401 || res.status === 403 || res.status === 404) return null;
  const data = await portalJson<{ mission?: DroneMissionView }>(res, 'fetchDroneMission');
  return data.mission ?? null;
}

export async function fetchMissionMessages(
  id: string,
  signal?: AbortSignal,
): Promise<MissionMessageView[]> {
  const res = await authedFetch(`/api/drone-missions/${id}/messages`, {
    signal,
    cache: 'no-store',
  });
  if (res.status === 401 || res.status === 403 || res.status === 404) return [];
  const data = await portalJson<{ messages?: MissionMessageView[] }>(res, 'fetchMissionMessages');
  return data.messages ?? [];
}

export async function postMissionMessage(id: string, content: string): Promise<MissionMessageView> {
  const data = await portalJson<{ message: MissionMessageView }>(
    await portalPost(`/api/drone-missions/${id}/messages`, { content }),
    'postMissionMessage',
  );
  return data.message;
}

export async function markMissionMessageRead(id: string, msgId: string): Promise<void> {
  await portalPatch(`/api/drone-missions/${id}/messages/${msgId}/read`, {});
}

/* --- operator side (portal routes) --- */

export async function fetchOperatorMissionMessages(
  id: string,
  signal?: AbortSignal,
): Promise<MissionMessageView[]> {
  const res = await authedFetch(`/api/operator-portal/missions/${id}/messages`, {
    signal,
    cache: 'no-store',
  });
  if (res.status === 401 || res.status === 404) return [];
  const data = await portalJson<{ messages?: MissionMessageView[] }>(
    res,
    'fetchOperatorMissionMessages',
  );
  return data.messages ?? [];
}

export async function postOperatorMissionMessage(
  id: string,
  content: string,
): Promise<MissionMessageView> {
  const data = await portalJson<{ message: MissionMessageView }>(
    await portalPost(`/api/operator-portal/missions/${id}/messages`, { content }),
    'postOperatorMissionMessage',
  );
  return data.message;
}

export async function markOperatorMissionMessageRead(id: string, msgId: string): Promise<void> {
  await portalPatch(`/api/operator-portal/missions/${id}/messages/${msgId}/read`, {});
}

/* ------------------------------------------------------------------ *
 * W.2 — Community render + Wellness/Headcount editor (over the W.1 API).
 * The community/headcount/wellness contracts live in @tsr/shared-types; only
 * the map FeatureCollection shape is web-local (the W.1 GET returns GeoJSON).
 * ------------------------------------------------------------------ */

export interface CommunityFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: CommunityFeatureProps;
}
export interface CommunityFeatureCollection {
  type: 'FeatureCollection';
  features: CommunityFeature[];
}

/** Bbox FeatureCollection for the map render (light — no wellness/headcount). */
export async function fetchCommunities(
  bbox: BboxQuery,
  signal: AbortSignal,
): Promise<CommunityFeatureCollection> {
  const params = new URLSearchParams({ bbox: bboxParam(bbox), limit: '1000' });
  const res = await fetch(`/api/communities?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchCommunities failed ${res.status}`);
  return (await res.json()) as CommunityFeatureCollection;
}

/**
 * #521 — the FULL FN + municipal jurisdiction roster (NO bbox), for the emergency-declaration
 * "declaring authority" PICKER that retires the fat-fingerable free-text. Returns every active
 * community (up to the 1000 cap) ordered by the server (population DESC, then name); the picker
 * groups them by kind. A read failure throws → the block falls back to the narrow free-text path.
 */
export async function fetchCommunityRoster(
  signal?: AbortSignal,
): Promise<CommunityFeatureCollection> {
  const res = await fetch('/api/communities?limit=1000', { signal });
  if (!res.ok) throw new Error(`fetchCommunityRoster failed ${res.status}`);
  return (await res.json()) as CommunityFeatureCollection;
}

/** Declare a community (W.2 editor; audited `declare_community`). */
export async function createCommunity(
  body: CommunityCreate,
  signal?: AbortSignal,
): Promise<Community> {
  const res = await fetch('/api/communities', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`createCommunity failed ${res.status}`);
  return (await res.json()) as Community;
}

/** Edit a community's identity/population OR move its centroid (W.2 editor). A
 *  lon+lat-only patch audits as `move_community`, else `edit_community`. */
export async function patchCommunity(
  id: string,
  body: CommunityPatch,
  signal?: AbortSignal,
): Promise<Community> {
  const res = await fetch(`/api/communities/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`patchCommunity failed ${res.status}`);
  return (await res.json()) as Community;
}

/** Soft-retire (`retire`) or un-retire (`restore`) a community (W.2 editor). */
export async function setCommunityRetired(
  id: string,
  retired: boolean,
  sessionId: string,
  signal?: AbortSignal,
): Promise<Community> {
  const res = await fetch(`/api/communities/${id}/${retired ? 'retire' : 'restore'}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(`setCommunityRetired failed ${res.status}`);
  return (await res.json()) as Community;
}

/** Detail + latest headcount + latest wellness check (drives the side panel +
 *  the client-side status cache that colours the map markers). */
export async function fetchCommunityDetail(
  id: string,
  signal?: AbortSignal,
): Promise<CommunityDetail> {
  const res = await fetch(`/api/communities/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchCommunityDetail failed ${res.status}`);
  return (await res.json()) as CommunityDetail;
}

/** Headcount snapshot history (newest first), for the side-panel accordion. */
export async function fetchCommunityHeadcounts(
  id: string,
  signal?: AbortSignal,
): Promise<CommunityHeadcount[]> {
  const res = await fetch(`/api/communities/${id}/headcounts?limit=50`, { signal });
  if (!res.ok) throw new Error(`fetchCommunityHeadcounts failed ${res.status}`);
  return ((await res.json()) as CommunityHeadcountListResponse).headcounts;
}

/** Wellness-check history (newest first), for the side-panel accordion. */
export async function fetchCommunityWellnessChecks(
  id: string,
  signal?: AbortSignal,
): Promise<CommunityWellnessCheck[]> {
  const res = await fetch(`/api/communities/${id}/wellness-checks?limit=50`, { signal });
  if (!res.ok) throw new Error(`fetchCommunityWellnessChecks failed ${res.status}`);
  return ((await res.json()) as CommunityWellnessCheckListResponse).wellness_checks;
}

/** #645 — BOTH arrival-attestation registers for a community (as_host / as_sending). READ is
 *  open (COP-common). Soft-empty on 4xx so a fresh community renders cleanly. NB: an absent
 *  register is total_attested_arrivals === null ("not reported by <host>", never 0). */
export async function fetchArrivalAttestations(
  id: string,
  signal?: AbortSignal,
): Promise<ArrivalAttestationsResponse> {
  const res = await fetch(`/api/communities/${id}/arrival-attestations`, { signal });
  if (res.status === 400 || res.status === 404)
    return { as_host: [], as_sending: [], total_attested_arrivals: null };
  if (!res.ok) throw new Error(`fetchArrivalAttestations failed ${res.status}`);
  return (await res.json()) as ArrivalAttestationsResponse;
}

/* ------------------------------------------------------------------ *
 * #420a — community coordination thread. AMBIENT (any operator viewing the community
 * reads + posts); the author is stamped server-side from the verified seat identity
 * (authedFetch forwards the `x-operator-id` seat header), never the body — so these
 * MUST go through authedFetch, not a bare fetch. No markRead fn (the per-message read
 * receipt is a deferred follow-on). `mine` rides each message from the server.
 * ------------------------------------------------------------------ */

/** The full coordination log for a community (oldest first). Soft-empty on 4xx so a
 *  fresh / unknown community renders an empty thread rather than throwing. */
export async function fetchCommunityMessages(
  communityId: string,
  signal?: AbortSignal,
): Promise<CommunityMessage[]> {
  const res = await authedFetch(`/api/communities/${communityId}/messages`, {
    signal,
    cache: 'no-store',
  });
  if (res.status === 400 || res.status === 401 || res.status === 403 || res.status === 404)
    return [];
  if (!res.ok) throw new Error(`fetchCommunityMessages failed ${res.status}`);
  return ((await res.json()) as { messages?: CommunityMessage[] }).messages ?? [];
}

/** Post to a community's coordination thread (author stamped server-side from the seat). The
 *  optional `hazard` binds the message to a fire (migration 0154) when posted from the OC-fire
 *  panel — legitimate client CONTEXT (which fire), not identity; both fields ride together. */
export async function postCommunityMessage(
  communityId: string,
  content: string,
  hazard?: { source_type: string; source_id: string },
): Promise<CommunityMessage> {
  const res = await authedFetch(`/api/communities/${communityId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      hazard
        ? { content, hazard_source_type: hazard.source_type, hazard_source_id: hazard.source_id }
        : { content },
    ),
  });
  if (!res.ok) throw new Error(`postCommunityMessage failed ${res.status}`);
  return ((await res.json()) as { message: CommunityMessage }).message;
}

/** #453 — EXPLICITLY acknowledge a community coordination message (records a per-message
 *  receipt; the reader identity is stamped server-side from the seat, never the body).
 *  Idempotent; returns the re-serialized message with its updated receipt state. */
export async function acknowledgeCommunityMessage(
  communityId: string,
  messageId: string,
  sessionId?: string,
): Promise<CommunityMessage> {
  const res = await authedFetch(
    `/api/communities/${communityId}/messages/${messageId}/acknowledge`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sessionId ? { session_id: sessionId } : {}),
    },
  );
  if (!res.ok) throw new Error(`acknowledgeCommunityMessage failed ${res.status}`);
  return ((await res.json()) as { message: CommunityMessage }).message;
}

/** #454 — the PEOC's MULTI-community coordination inbox: one row per community with traffic
 *  (unread-for-me OR recently active). The `unread_count` is the VIEWING seat's OWN
 *  not-yet-acknowledged messages, so the response is per-viewer (no cache).
 *
 *  HONESTY (unknown ≠ zero): unlike fetchCommunityMessages (which soft-empties a fresh/unknown
 *  community's thread on 4xx), this THROWS on any non-OK response — a failed inbox load must
 *  render the panel's ERROR state, never an "all quiet" empty list or a "0" badge. */
export async function fetchCommunityCoordinationInbox(
  signal?: AbortSignal,
): Promise<CommunityCoordinationInboxRow[]> {
  const res = await authedFetch('/api/communities/comms-inbox', { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`fetchCommunityCoordinationInbox failed ${res.status}`);
  return (
    ((await res.json()) as { communities?: CommunityCoordinationInboxRow[] }).communities ?? []
  );
}

/** Declare a headcount snapshot (audited `declare_headcount`). */
export async function createCommunityHeadcount(
  communityId: string,
  body: HeadcountCreate,
  signal?: AbortSignal,
): Promise<CommunityHeadcount> {
  const res = await fetch(`/api/communities/${communityId}/headcounts`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`createCommunityHeadcount failed ${res.status}`);
  return (await res.json()) as CommunityHeadcount;
}

/** #6a — list this operator's SMS notification subscriptions (optionally scoped to a
 *  community). The phone is returned MASKED; the PIN is never serialized. */
export async function fetchNotificationSubscriptions(
  communityId?: string,
  signal?: AbortSignal,
): Promise<NotificationSubscription[]> {
  const qs = communityId ? `?community_id=${encodeURIComponent(communityId)}` : '';
  const res = await fetch(`/api/notification-subscriptions${qs}`, { signal });
  if (!res.ok) throw new Error(`fetchNotificationSubscriptions failed ${res.status}`);
  return ((await res.json()) as { subscriptions: NotificationSubscription[] }).subscriptions;
}

/** #6a — opt in to jurisdiction-scoped SMS (audited `update_notification_subscription`;
 *  creates a PENDING subscription + a one-time PIN the worker texts to confirm). */
export async function createNotificationSubscription(
  body: NotificationSubscriptionCreate,
  signal?: AbortSignal,
): Promise<NotificationSubscription> {
  const res = await fetch('/api/notification-subscriptions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`createNotificationSubscription failed ${res.status}`);
  return ((await res.json()) as { subscription: NotificationSubscription }).subscription;
}

/** #6a — edit per-trigger toggles or opt out (audited `update_notification_subscription`). */
export async function patchNotificationSubscription(
  id: string,
  body: NotificationSubscriptionPatch,
  signal?: AbortSignal,
): Promise<NotificationSubscription> {
  const res = await fetch(`/api/notification-subscriptions/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`patchNotificationSubscription failed ${res.status}`);
  return ((await res.json()) as { subscription: NotificationSubscription }).subscription;
}

/** Start a wellness check (audited `start_wellness_check`). */
export async function createCommunityWellnessCheck(
  communityId: string,
  body: WellnessCheckCreate,
  signal?: AbortSignal,
): Promise<CommunityWellnessCheck> {
  const res = await fetch(`/api/communities/${communityId}/wellness-checks`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`createCommunityWellnessCheck failed ${res.status}`);
  return (await res.json()) as CommunityWellnessCheck;
}

/** Update a wellness check (audited `update_wellness_check` /
 *  `complete_wellness_check`). Throws on a 409 invalid status transition. */
export async function updateCommunityWellnessCheck(
  wellnessCheckId: string,
  body: WellnessCheckPatch,
  signal?: AbortSignal,
): Promise<CommunityWellnessCheck> {
  const res = await fetch(`/api/wellness-checks/${wellnessCheckId}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    if (res.status === 409) throw new Error('invalid_transition');
    throw new Error(`updateCommunityWellnessCheck failed ${res.status}`);
  }
  return (await res.json()) as CommunityWellnessCheck;
}

/* ---- R.3 click-fire UX: nearby resources + dispatch recommendations ---- */

/** Nearby response assets for an incident (R.3.2). */
export async function fetchNearbyAssets(
  lat: number,
  lon: number,
  signal?: AbortSignal,
  opts: { radiusKm?: number; limit?: number } = {},
): Promise<NearbyAsset[]> {
  const params = new URLSearchParams({ lat: String(lat), lon: String(lon) });
  if (opts.radiusKm != null) params.set('radius_km', String(opts.radiusKm));
  if (opts.limit != null) params.set('limit', String(opts.limit));
  const res = await fetch(`/api/response-assets/nearest?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchNearbyAssets failed ${res.status}`);
  return ((await res.json()) as { assets: NearbyAsset[] }).assets;
}

/** Live aircraft near an incident (R.3.3). */
export async function fetchNearbyAircraft(
  lat: number,
  lon: number,
  signal?: AbortSignal,
  opts: { radiusKm?: number; limit?: number } = {},
): Promise<NearbyAircraft[]> {
  const params = new URLSearchParams({ lat: String(lat), lon: String(lon) });
  if (opts.radiusKm != null) params.set('radius_km', String(opts.radiusKm));
  if (opts.limit != null) params.set('limit', String(opts.limit));
  const res = await fetch(`/api/aircraft/nearest?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchNearbyAircraft failed ${res.status}`);
  return ((await res.json()) as { aircraft: NearbyAircraft[] }).aircraft;
}

/** Create a dispatch recommendation (R.3.5) — the "dispatch" leg of the flagship
 *  workflow. Returns the created row (id + recommended assets + status). */
export async function createDispatchRecommendation(
  body: DispatchRecommendationRequest,
  signal?: AbortSignal,
): Promise<DispatchRecommendation> {
  const res = await fetch('/api/dispatch-recommendations', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`createDispatchRecommendation failed ${res.status}`);
  return (await res.json()) as DispatchRecommendation;
}

/** Forward a dispatch recommendation to the responding agency via the real
 *  §4.21 delivery channel (migration 0047). The second, distinct step of
 *  "recommend → forward"; the system never auto-sends. */
export async function forwardDispatchRecommendation(
  id: string,
  sessionId: string,
  destinationId?: string,
  signal?: AbortSignal,
): Promise<DeliveryResult> {
  const res = await fetch(`/api/dispatch-recommendations/${id}/forward`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, destination_id: destinationId }),
    signal,
  });
  if (!res.ok) throw new Error(`forwardDispatchRecommendation failed ${res.status}`);
  return (await res.json()) as DeliveryResult;
}

/** Resolve the tracked incident a source record was promoted to (Task #92), or
 *  null if it hasn't been promoted yet. Used by the FireBody panel to find the
 *  incident its resource requests are linked to (after an active_fire dispatch). */
export async function fetchIncidentForSource(
  sourceRecordType: IncidentSourceRecordType,
  sourceRecordId: string,
  signal?: AbortSignal,
): Promise<Incident | null> {
  const params = new URLSearchParams({
    source_record_type: sourceRecordType,
    source_record_id: sourceRecordId,
    limit: '1',
  });
  const res = await fetch(`/api/incidents?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchIncidentForSource failed ${res.status}`);
  const body = (await res.json()) as { incidents: Incident[] };
  return body.incidents[0] ?? null;
}

/** List tracked incidents for the Coordination → Incidents rail tab (#387). Opens
 *  to the live worklist (status:'open' by default); the panel polls this on the
 *  same 30s cadence as the other rail surfaces so a freshly-declared incident
 *  appears without a manual reload. Read-only — declaring/closing stays on the
 *  incident detail surface. */
export async function fetchIncidents(
  opts: {
    status?: 'open' | 'closed';
    limit?: number;
    /** #485 — lifecycle-phase filter (the "Recently deactivated" surface reads
     *  status=closed + lifecycle_state=deactivated). */
    lifecycle_state?: IncidentLifecycleState;
    /** #485 — the recently-deactivated window (days); when set the list is bounded to
     *  incidents closed within N days AND ordered most-recently-deactivated first. */
    deactivated_within_days?: number;
    /** #485 — proximity box [west, south, east, north] (the promote reopen-nudge scopes
     *  nearby recently-deactivated incidents to a ~25 km box around the hazard). */
    bbox?: [number, number, number, number];
  } = {},
  signal?: AbortSignal,
): Promise<Incident[]> {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.lifecycle_state) params.set('lifecycle_state', opts.lifecycle_state);
  if (opts.deactivated_within_days != null)
    params.set('deactivated_within_days', String(opts.deactivated_within_days));
  if (opts.bbox) params.set('bbox', opts.bbox.join(','));
  params.set('limit', String(opts.limit ?? 100));
  const res = await fetch(`/api/incidents?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchIncidents failed ${res.status}`);
  const body = (await res.json()) as { incidents: Incident[] };
  return body.incidents;
}

/** Promote a source record to a tracked incident (idempotent server-side: a
 *  second promote of the same record returns the existing one). Used by the
 *  sectoring flow to ensure a fire/hotspot has a real incident to attach a
 *  sector to before drawing. */
export async function createIncident(
  body: {
    session_id: string;
    name: string;
    hazard_type: string;
    source_record_type?: IncidentSourceRecordType;
    source_record_id?: string;
    lon?: number;
    lat?: number;
  },
  signal?: AbortSignal,
): Promise<Incident> {
  const res = await fetch('/api/incidents', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `createIncident failed ${res.status}`));
  return (await res.json()) as Incident;
}

/* ------------------------------------------------------------------ *
 * R5 Area Command Slice 2 (#494) — membership proposal + establish / place / remove.
 * Reuses the shipped POST /api/area-commands[/place|/remove] verbs (no new verb) and the
 * GET /api/area-commands/proposals read. NEVER auto-merges — the operator confirms.
 * ------------------------------------------------------------------ */

/** Read-time Area Command grouping PROPOSALS over ungrouped open wildfire incidents. */
export async function fetchAreaCommandProposals(
  signal?: AbortSignal,
): Promise<AreaCommandProposal[]> {
  const res = await fetch('/api/area-commands/proposals', { signal });
  if (!res.ok) throw new Error(`fetchAreaCommandProposals failed ${res.status}`);
  const body = (await res.json()) as { proposals: AreaCommandProposal[] };
  return body.proposals;
}

/** The OPEN Area Command parents (is_area_command=true) — for the rail's Area Commands
 *  section + the child-row "Under Area Command — <name>" badge name map. */
export async function fetchAreaCommands(signal?: AbortSignal): Promise<Incident[]> {
  const res = await fetch('/api/incidents?include_area_commands=true&status=open&limit=200', {
    signal,
  });
  if (!res.ok) throw new Error(`fetchAreaCommands failed ${res.status}`);
  const body = (await res.json()) as { incidents: Incident[] };
  return body.incidents.filter((i) => i.is_area_command);
}

/** ESTABLISH a new Area Command (§4.6 oversight parent). Returns the created incident. */
export async function establishAreaCommand(
  input: { name: string; lon: number; lat: number },
  sessionId: string,
  signal?: AbortSignal,
): Promise<Incident> {
  const res = await fetch('/api/area-commands', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...input, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `establishAreaCommand failed ${res.status}`));
  return (await res.json()) as Incident;
}

/** Outcome of a place — distinguishes the 409 (child already under a DIFFERENT Area
 *  Command) so the caller can DROP that child + re-render rather than fail the establish. */
export type PlaceUnderAreaCommandResult =
  | { placed: true; conflict: false }
  | { placed: false; conflict: true; message: string };

/** PLACE a child incident under an Area Command. A 409 is RETURNED (not thrown) so a
 *  confirm loop can drop the conflicting child and continue; any OTHER failure throws. */
export async function placeUnderAreaCommand(
  parentIncidentId: string,
  childIncidentId: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<PlaceUnderAreaCommandResult> {
  const res = await fetch('/api/area-commands/place', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      parent_incident_id: parentIncidentId,
      child_incident_id: childIncidentId,
      session_id: sessionId,
    }),
    signal,
  });
  if (res.status === 409) {
    return {
      placed: false,
      conflict: true,
      message: await errorMessage(res, 'already under a different Area Command'),
    };
  }
  if (!res.ok)
    throw new Error(await errorMessage(res, `placeUnderAreaCommand failed ${res.status}`));
  return { placed: true, conflict: false };
}

/** REMOVE a child incident from its Area Command (reversible; child survives ungrouped). */
export async function removeFromAreaCommand(
  childIncidentId: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/area-commands/remove', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ child_incident_id: childIncidentId, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `removeFromAreaCommand failed ${res.status}`));
}

/**
 * R5 Area Command Slice 5 (#541) — REPLACE the Area Command PARENT's ordered BROAD
 * OBJECTIVES (Ontario IMS §4.6 / App A #2). The array order IS the objectives' order;
 * an empty array clears them. PUTs to the is_area_command parent (422 otherwise).
 */
export async function setAreaCommandObjectives(
  areaCommandId: string,
  objectives: string[],
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/area-commands/${areaCommandId}/objectives`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ objectives, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `setAreaCommandObjectives failed ${res.status}`));
}

/**
 * R5 Area Command Slice 5 (#541) — REPLACE the Area Command PARENT's ordered PRIORITIES
 * for the sharing of critical resources across sites (App A #2). A priority STATEMENT —
 * never a child allocation/tasking (§4.6). Same replace-whole-ordered-list + gate.
 */
export async function setAreaCommandResourcePriorities(
  areaCommandId: string,
  resourcePriorities: string[],
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/area-commands/${areaCommandId}/resource-priorities`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ resource_priorities: resourcePriorities, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(
      await errorMessage(res, `setAreaCommandResourcePriorities failed ${res.status}`),
    );
}

/* ------------------------------------------------------------------ *
 * Incident & Operational-Period management (SITL surface, v3.1–v3.3).
 * Read + the existing close / declare / transition / cadence verbs — no new
 * endpoint, no migration. Powers IncidentManagementSection.
 * ------------------------------------------------------------------ */

/** Full incident detail: incident + its operational periods + the SITREP chain
 *  (each ref carries the read-derived `auto_generated` provenance flag). */
export async function fetchIncidentDetail(
  id: string,
  signal?: AbortSignal,
): Promise<IncidentDetail> {
  // no-store: the management section refetches immediately after a write (declare /
  // transition / cadence / close), so it must bypass the endpoint's 30s HTTP cache
  // or the operator would see their own action served stale.
  const res = await fetch(`/api/incidents/${id}`, { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`fetchIncidentDetail failed ${res.status}`);
  return (await res.json()) as IncidentDetail;
}

/** AAR time replay (#180): the incident's clock bounds, incident-scoped audit
 *  events and nearby anomaly lifecycle timestamps — a pure read-model. */
export async function fetchIncidentReplay(
  id: string,
  signal?: AbortSignal,
): Promise<AarReplayResponse> {
  const res = await fetch(`/api/incidents/${id}/replay`, { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`fetchIncidentReplay failed ${res.status}`);
  return (await res.json()) as AarReplayResponse;
}

/** Close a tracked incident (PATCH status:'closed' → close_incident verb). */
export async function closeIncident(
  id: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<Incident> {
  const res = await fetch(`/api/incidents/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ status: 'closed', session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `closeIncident failed ${res.status}`));
  return (await res.json()) as Incident;
}

/** Advance/revert an incident's lifecycle phase (R1/#477 — POST /:id/advance →
 *  advance_incident_lifecycle verb). `to` must be an immediate neighbour of the current
 *  state (forward one phase OR de-escalate one); the server 409s on an illegal hop and
 *  couples status (deactivated⇒closed, reignition⇒reopen). */
export async function advanceIncidentLifecycle(
  id: string,
  to: IncidentLifecycleState,
  sessionId: string,
  signal?: AbortSignal,
): Promise<Incident> {
  const res = await fetch(`/api/incidents/${id}/advance`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ to, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `advanceIncidentLifecycle failed ${res.status}`));
  return (await res.json()) as Incident;
}

/* ------------------------------------------------------------------ *
 * W3 spine (#489) — structured Incident Command (single command)
 * ------------------------------------------------------------------ */

/** The lead-organization + command-officer capture for assume-command. */
export interface AssumeCommandInput {
  agency_id: string;
  officer_name: string;
  officer_phone?: string | null;
  officer_email?: string | null;
}

/** ESTABLISH single Incident Command (POST /:id/assume-command → assume_incident_command
 *  verb): creates the lead incident_command row AND advances the incident confirmed→owned.
 *  409 if the incident is not 'confirmed'; 422 if the lead is not a commanding jurisdiction. */
export async function assumeIncidentCommand(
  id: string,
  input: AssumeCommandInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<Incident> {
  const res = await fetch(`/api/incidents/${id}/assume-command`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...input, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `assumeIncidentCommand failed ${res.status}`));
  return (await res.json()) as Incident;
}

/** RELEASE a command participation (POST /:id/release-command → release_incident_command verb).
 *  `agencyId` omitted (or = the lead) → the single-command release (reverts owned→confirmed;
 *  409 if unified co-leads remain). `agencyId` = a live co-lead → that co-lead leaves unified
 *  command and the incident stays owned (the §4.7 return-to-single-command path). */
export async function releaseIncidentCommand(
  id: string,
  sessionId: string,
  agencyId?: string,
  signal?: AbortSignal,
): Promise<Incident> {
  const res = await fetch(`/api/incidents/${id}/release-command`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, ...(agencyId ? { agency_id: agencyId } : {}) }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `releaseIncidentCommand failed ${res.status}`));
  return (await res.json()) as Incident;
}

/** #445-ws2 — the result of a Response Level SET. `action` is the operator-facing word for
 *  the transition (Set / Escalate / De-escalate — never "Declare"). */
export interface SetIncidentResponseLevelResult {
  incident_id: string;
  incident_number: string;
  operator_set_response_level: number;
  from: number | null;
  to: number;
  action: 'Set' | 'Escalate' | 'De-escalate';
  assessed_by: string;
}

/** #445-ws2 — the Incident Commander SETs / Escalates / De-escalates the OPERATOR-ASSESSED
 *  Response Level (POST /:id/response-level → set_incident_response_level verb). The reason is
 *  REQUIRED (records-not-confers assessment note); the level is attributed to the live command
 *  officer. Gated to an OWNED incident (409 not_owned otherwise). NEVER a declaration. */
export async function setIncidentResponseLevel(
  id: string,
  responseLevel: number,
  reason: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<SetIncidentResponseLevelResult> {
  const res = await fetch(`/api/incidents/${id}/response-level`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ response_level: responseLevel, reason, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `setIncidentResponseLevel failed ${res.status}`));
  return (await res.json()) as SetIncidentResponseLevelResult;
}

/** #445-ws2 — CLEAR the operator pin (DELETE /:id/response-level → clear_incident_response_level
 *  verb). The incident reverts to the SYSTEM-DERIVED Response Level. Reverting to the honest
 *  derived can never bury a hazard, so this is ungated (any tenant-visible operator may clear). */
export async function clearIncidentResponseLevel(
  id: string,
  sessionId: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<{ incident_id: string; cleared: boolean; from: number | null }> {
  const res = await fetch(`/api/incidents/${id}/response-level`, {
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, ...(reason ? { reason } : {}) }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `clearIncidentResponseLevel failed ${res.status}`));
  return (await res.json()) as { incident_id: string; cleared: boolean; from: number | null };
}

/** The capture for INVITE TO UNIFIED COMMAND (§4.7) — the invited lead organization + its
 *  functional Operations branch + command officer + the REQUIRED signed agreement. */
export interface InviteToUnifiedCommandInput {
  agency_id: string;
  agreement_note: string;
  functional_role?: string | null;
  officer_name: string;
  officer_phone?: string | null;
  officer_email?: string | null;
}

/** INVITE TO UNIFIED COMMAND (POST /:id/invite-unified-command → invite_to_unified_command verb;
 *  #481 MUST-2, migration 0147): the current lead ISSUES a §4.7 unified invite. NO co-lead is added
 *  — the command is unchanged until the invitee accepts (join_unified_command on acknowledge-transfer),
 *  the two-sided fix for the old unilateral join. 422 if the invited org is not a commanding
 *  jurisdiction; 409 if it already shares command or a command change is already pending. */
export async function inviteToUnifiedCommand(
  id: string,
  input: InviteToUnifiedCommandInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<Incident> {
  const res = await fetch(`/api/incidents/${id}/invite-unified-command`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...input, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `inviteToUnifiedCommand failed ${res.status}`));
  return (await res.json()) as Incident;
}

/** The receiving-lead + hand-over-briefing capture for TRANSFER OF COMMAND (§4.8). */
export interface TransferCommandInput {
  to_agency_id: string;
  handover_briefing: string;
  to_officer_name: string;
  to_officer_phone?: string | null;
  to_officer_email?: string | null;
}

/** ISSUE a TRANSFER OF COMMAND (POST /:id/transfer-command → transfer_incident_command verb):
 *  the outgoing lead hands command over to another commanding organization with a REQUIRED
 *  hand-over briefing. Command does NOT move until acknowledged. 409 if a transfer is already
 *  pending / the briefing is empty; 422 if the receiving organization is not a commanding seat. */
export async function transferIncidentCommand(
  id: string,
  input: TransferCommandInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<Incident> {
  const res = await fetch(`/api/incidents/${id}/transfer-command`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...input, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `transferIncidentCommand failed ${res.status}`));
  return (await res.json()) as Incident;
}

/** ACCEPT the pending command change (POST /:id/acknowledge-transfer): the receiving/invited IC
 *  accepts. The server branches on the pending row's kind — a §4.8 transfer moves the lead A → B
 *  (acknowledge_incident_transfer verb); a §4.7 unified invite adds the co-lead and the command
 *  becomes unified (join_unified_command verb, migration 0147). 409 if there is no pending change. */
export async function acknowledgeIncidentTransfer(
  id: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<Incident> {
  const res = await fetch(`/api/incidents/${id}/acknowledge-transfer`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `acknowledgeIncidentTransfer failed ${res.status}`));
  return (await res.json()) as Incident;
}

/** The RECEIVER's inbound worklist of pending command hand-overs addressed to this agency (#481
 *  MUST-1) — pending §4.8 transfers + §4.7 unified invites where to_agency = the viewer. Mirrors
 *  fetchInboxAgencyTasks (target-scoped). The Coordination Inbox renders these; a row opens the
 *  incident record via the `#incident=<id>` deep-link, where the receiving IC accepts. */
export async function fetchCommandHandoverInbox(
  signal?: AbortSignal,
): Promise<CommandHandoverInboxItem[]> {
  const res = await fetch('/api/incidents/command-handovers/inbox', { signal });
  if (!res.ok)
    throw new Error(await errorMessage(res, `fetchCommandHandoverInbox failed ${res.status}`));
  const data = (await res.json()) as { handovers?: CommandHandoverInboxItem[] };
  return data.handovers ?? [];
}

/* ------------------------------------------------------------------ *
 * Declaration & Notification Slice 1 (#495) — emergency declaration + Notify PEOC
 * ------------------------------------------------------------------ */

/** The capture for a declaration of emergency (records-not-confers). BOTH authority kinds:
 *  municipal (Head of Council) and first_nation (Band Council by BCR). */
export interface DeclareEmergencyInput {
  authority_kind: DeclarationAuthorityKind;
  declaring_authority_name: string;
  declarer_name?: string;
  /** Band Council Resolution reference (first_nation only; omitted ⇒ "to follow"). */
  bcr_reference?: string;
  /** Order-in-Council reference (provincial only, EMCPA s.7.0.1; omitted ⇒ "Order in Council to follow"). */
  oic_reference?: string;
  /** Provincial declared area ("throughout Ontario" | a named part of Ontario; provincial only). */
  provincial_declared_area?: string;
  incident_id?: string;
  hazard_type?: string;
  notes?: string;
}

/** List emergency declarations (agency-scoped). */
export async function fetchEmergencyDeclarations(
  signal?: AbortSignal,
): Promise<EmergencyDeclarationRecord[]> {
  const res = await fetch('/api/emergency-declarations', { signal, cache: 'no-store' });
  if (!res.ok)
    throw new Error(await errorMessage(res, `fetchEmergencyDeclarations failed ${res.status}`));
  return ((await res.json()) as { declarations: EmergencyDeclarationRecord[] }).declarations;
}

/** W4b — the unified PEOC Arrivals feed (declarations + FN dual-notify badge + Area-Command
 *  establishments). Agency-scoped read-model; the FN dual-notify is the isc_notified_at
 *  badge on a declaration row, not a separate arrival. */
export async function fetchPeocArrivals(signal?: AbortSignal): Promise<PeocArrival[]> {
  const res = await fetch('/api/peoc-arrivals', { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(await errorMessage(res, `fetchPeocArrivals failed ${res.status}`));
  return ((await res.json()) as { arrivals: PeocArrival[] }).arrivals;
}

/** W4b — RECORD a PEOC receipt on an arrival (records receipt ONLY — never approve/command/
 *  check-in). Idempotent server-side; never mutates the source declaration/incident. */
export async function acknowledgePeocArrival(
  sourceType: PeocArrival['source_type'],
  sourceId: string,
  sessionId?: string,
  signal?: AbortSignal,
): Promise<{
  source_type: string;
  source_id: string;
  acknowledged_at: string;
  acknowledged_by: string | null;
}> {
  const res = await fetch(`/api/peoc-arrivals/${sourceType}/${sourceId}/acknowledge`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `acknowledgePeocArrival failed ${res.status}`));
  return (await res.json()) as {
    source_type: string;
    source_id: string;
    acknowledged_at: string;
    acknowledged_by: string | null;
  };
}

/* ------------------------------------------------------------------ *
 * #328 Phase 2 — the two-way inter-ops information-sharing thread (migration 0135).
 * COP-side plain-fetch client (mirrors the peoc-arrivals fns). Send fans out to N
 * recipients; acknowledge records a per-recipient RECEIPT (idempotent). The roll-up
 * assembles the incident's "Information sharing" list (R2/R3 recorded legs only).
 * ------------------------------------------------------------------ */

export interface SendInteropsMessageInput {
  context_type: InteropsContextType;
  context_id?: string | null;
  kind: InteropsMessageKind;
  body: string;
  in_reply_to?: string;
  recipients: Array<{ agency_id?: string; destination_id?: string }>;
}

/** Send one inter-ops message, fanned out to its recipients. Returns the created message
 *  (with its recorded per-recipient delivery/receipt state). */
export async function sendInteropsMessage(
  input: SendInteropsMessageInput,
  sessionId?: string,
  signal?: AbortSignal,
): Promise<InteropsMessageView> {
  const res = await fetch('/api/interops/messages', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...input, session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `sendInteropsMessage failed ${res.status}`));
  return ((await res.json()) as { message: InteropsMessageView }).message;
}

/** Acknowledge a message for a recipient (records a RECEIPT only — never mutates the message).
 *  Idempotent; an out-of-app leg cannot be acknowledged (the server 422s). */
export async function acknowledgeInteropsMessage(
  messageId: string,
  recipientId?: string,
  sessionId?: string,
  acknowledgedBy?: string,
  signal?: AbortSignal,
): Promise<{ ok: boolean; recipient_id: string }> {
  const res = await fetch(`/api/interops/messages/${messageId}/acknowledge`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      recipient_id: recipientId,
      acknowledged_by: acknowledgedBy,
      session_id: sessionId,
    }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `acknowledgeInteropsMessage failed ${res.status}`));
  return (await res.json()) as { ok: boolean; recipient_id: string };
}

/** Record an OUT-OF-APP reply (#567) — paste back a response received off-app on an out-of-app
 *  leg of an inter-ops message; it joins the thread as a recorded document (kind
 *  'recorded_response'). A record, never a send. Returns the created recorded message. The leg
 *  must be an out-of-app recipient of the message (the server 400s an in-app leg). */
export async function recordInteropsResponse(
  messageId: string,
  recipientId: string,
  body: string,
  sessionId?: string,
  signal?: AbortSignal,
): Promise<InteropsMessageView> {
  const res = await fetch(`/api/interops/messages/${messageId}/record-response`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ recipient_id: recipientId, body, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `recordInteropsResponse failed ${res.status}`));
  return ((await res.json()) as { message: InteropsMessageView }).message;
}

/** Load a context-attached inter-ops thread (oldest first), each message with its recipients. */
export async function fetchInteropsThread(
  contextType: InteropsContextType,
  contextId: string | null,
  signal?: AbortSignal,
): Promise<InteropsThreadView> {
  const qs = new URLSearchParams({ context_type: contextType });
  if (contextId) qs.set('context_id', contextId);
  const res = await fetch(`/api/interops/threads?${qs.toString()}`, { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(await errorMessage(res, `fetchInteropsThread failed ${res.status}`));
  return (await res.json()) as InteropsThreadView;
}

/** Assemble the incident's "Information sharing" roll-up (interops messages + inbound assistance
 *  requests + outbound agency tasks), one list. Recorded onward legs only (R3). */
export async function fetchInteropsRollup(
  incidentId: string,
  signal?: AbortSignal,
): Promise<InteropsRollupView> {
  const res = await fetch(`/api/interops/rollup?incident_id=${encodeURIComponent(incidentId)}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(await errorMessage(res, `fetchInteropsRollup failed ${res.status}`));
  return (await res.json()) as InteropsRollupView;
}

/** W4c DUTY WATCH — read THIS desk's watch state for a file (incident / assistance_request /
 *  agency_task). `watching` = the current desk is on watch; `count` = active desks watching
 *  (cross-tenant aggregate — DORMANT until #190; reads 0/1 in the single-seat demo). */
export async function fetchDeskWatch(
  sourceType: WatchSourceType,
  sourceId: string,
  signal?: AbortSignal,
): Promise<DeskWatchState> {
  const res = await fetch(`/api/desk-watches/${sourceType}/${sourceId}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(await errorMessage(res, `fetchDeskWatch failed ${res.status}`));
  return (await res.json()) as DeskWatchState;
}

/** W4c DUTY WATCH — set (or clear) THIS desk's watch on a file. `active: true` = Watch;
 *  `active: false` = Stop watching. NON-EXCLUSIVE + additive — never evicts another desk's
 *  watch, never a claim. Audited `set_desk_watch`. Returns the refreshed state. */
export async function setDeskWatch(
  sourceType: WatchSourceType,
  sourceId: string,
  active: boolean,
  sessionId?: string,
  signal?: AbortSignal,
): Promise<DeskWatchState> {
  const res = await fetch(`/api/desk-watches/${sourceType}/${sourceId}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ active, session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `setDeskWatch failed ${res.status}`));
  return (await res.json()) as DeskWatchState;
}

/** DECLARE an emergency (POST /api/emergency-declarations → declare_emergency verb). The COP
 *  RECORDS on behalf of the declaring authority (Head of Council / Band Council); it does not
 *  legally issue the declaration. */
export async function declareEmergency(
  input: DeclareEmergencyInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<EmergencyDeclarationRecord> {
  const body: EmergencyDeclarationCreate = {
    session_id: sessionId,
    ...input,
  };
  const res = await fetch('/api/emergency-declarations', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `declareEmergency failed ${res.status}`));
  return (await res.json()) as EmergencyDeclarationRecord;
}

/** BACKFILL the Band Council Resolution reference (PATCH /:id/bcr; first_nation only). PERP
 *  §6.6.4: a First Nation may verbally declare "with a band council resolution to follow". */
export async function backfillDeclarationBcr(
  id: string,
  bcrReference: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<EmergencyDeclarationRecord> {
  const res = await fetch(`/api/emergency-declarations/${id}/bcr`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, bcr_reference: bcrReference }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `backfillDeclarationBcr failed ${res.status}`));
  return (await res.json()) as EmergencyDeclarationRecord;
}

/** TERMINATE a declaration of emergency (POST /:id/terminate → terminate_emergency_declaration).
 *  `terminatingBcrReference` (first_nation) records the subsequent BCR that ends it. */
export async function terminateEmergencyDeclaration(
  id: string,
  sessionId: string,
  opts: { reason?: string; terminatingBcrReference?: string } = {},
  signal?: AbortSignal,
): Promise<EmergencyDeclarationRecord> {
  const res = await fetch(`/api/emergency-declarations/${id}/terminate`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      reason: opts.reason,
      terminating_bcr_reference: opts.terminatingBcrReference,
    }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `terminateEmergencyDeclaration failed ${res.status}`));
  return (await res.json()) as EmergencyDeclarationRecord;
}

/** NOTIFY THE PEOC (POST /:id/notify-peoc → notify_peoc_declaration verb; the #490 bridge).
 *  `callConfirmed` records that the operator also placed the s.4(3) phone call. */
export async function notifyPeocDeclaration(
  id: string,
  sessionId: string,
  opts: { callConfirmed?: boolean; callNotes?: string; destinationId?: string } = {},
  signal?: AbortSignal,
): Promise<EmergencyDeclarationNotifyResult> {
  const res = await fetch(`/api/emergency-declarations/${id}/notify-peoc`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      call_confirmed: opts.callConfirmed ?? false,
      call_notes: opts.callNotes,
      destination_id: opts.destinationId,
    }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `notifyPeocDeclaration failed ${res.status}`));
  return (await res.json()) as EmergencyDeclarationNotifyResult;
}

/** NOTIFY the PEOC and Indigenous Services Canada (POST /:id/notify-peoc-isc → the shared
 *  notify_peoc_declaration verb; first_nation only). ONE act, two GOVERNMENT legs (PERP §6.6.4 +
 *  §6.7) — records-not-confers, never a public alert. */
export async function notifyPeocIscDeclaration(
  id: string,
  sessionId: string,
  opts: { callConfirmed?: boolean; callNotes?: string } = {},
  signal?: AbortSignal,
): Promise<EmergencyDeclarationDualNotifyResult> {
  const res = await fetch(`/api/emergency-declarations/${id}/notify-peoc-isc`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      call_confirmed: opts.callConfirmed ?? false,
      call_notes: opts.callNotes,
    }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `notifyPeocIscDeclaration failed ${res.status}`));
  return (await res.json()) as EmergencyDeclarationDualNotifyResult;
}

/** RECORD the Bill-25 s.4(10) post-termination report to the Minister (POST /:id/record-report →
 *  record_post_termination_report verb; MUNICIPAL only, POST-termination only). The COP RECORDS
 *  the report on behalf of the head of council — it does NOT submit it. Slice-4 dormant UI. */
export async function recordDeclarationReport(
  id: string,
  sessionId: string,
  opts: { explanation?: string; prescribedInfo?: Record<string, unknown> } = {},
  signal?: AbortSignal,
): Promise<EmergencyDeclarationRecord> {
  const res = await fetch(`/api/emergency-declarations/${id}/record-report`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      // #529 — the s.4(10)(c) explanation + (10)(d) prescribed info (dormant capture; the report
      // UI is gated on BILL25_S4_IN_FORCE). Omitted keys drop out of the JSON payload.
      explanation: opts.explanation,
      prescribed_info: opts.prescribedInfo,
    }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `recordDeclarationReport failed ${res.status}`));
  return (await res.json()) as EmergencyDeclarationRecord;
}

/** RECORD the PROVINCIAL closeout — the Premier's s.7.0.10 report to the Assembly (POST
 *  /:id/record-assembly-report → reuses the record_post_termination_report verb; PROVINCIAL only,
 *  POST-termination only). The COP RECORDS that the Premier tabled it — it does NOT table it. LIVE
 *  (in-force EMCPA — NOT the Bill-25-gated municipal s.4(10) report). */
export async function recordProvincialCloseout(
  id: string,
  sessionId: string,
  opts: {
    tabledAt?: string;
    assemblyReportReference?: string;
    assemblyReportSummary?: string;
  } = {},
  signal?: AbortSignal,
): Promise<EmergencyDeclarationRecord> {
  const res = await fetch(`/api/emergency-declarations/${id}/record-assembly-report`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      // Optional: the actual date the Premier tabled the report (defaults route-side to now()),
      // the Sessional Paper number, and a short summary. Omitted keys drop out of the JSON payload.
      tabled_at: opts.tabledAt,
      assembly_report_reference: opts.assemblyReportReference,
      assembly_report_summary: opts.assemblyReportSummary,
    }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `recordProvincialCloseout failed ${res.status}`));
  return (await res.json()) as EmergencyDeclarationRecord;
}

/* ------------------------------------------------------------------ *
 * Declaration & Notification Slice 2 (#497) — public-alert REQUEST to the PEOC
 * ------------------------------------------------------------------ */

/** The capture for a public-alert REQUEST (records-not-confers). BOTH authority kinds. */
export interface RequestPublicAlertInput {
  authority_kind: 'municipal' | 'first_nation';
  requesting_authority_name: string;
  requester_name?: string;
  incident_id?: string;
  /** #461 — provenance: the evacuation order this request was raised FROM (the bridge). */
  evacuation_order_id?: string;
  hazard_type?: string;
  notes?: string;
  call_confirmed?: boolean;
  call_notes?: string;
}

/** List public-alert requests (agency-scoped). */
export async function fetchPublicAlertRequests(
  signal?: AbortSignal,
): Promise<PublicAlertRequestRecord[]> {
  const res = await fetch('/api/public-alert-requests', { signal, cache: 'no-store' });
  if (!res.ok)
    throw new Error(await errorMessage(res, `fetchPublicAlertRequests failed ${res.status}`));
  return ((await res.json()) as { requests: PublicAlertRequestRecord[] }).requests;
}

/** REQUEST a public alert (POST /api/public-alert-requests → request_public_alert verb).
 *  The COP RECORDS the request on behalf of the authority + routes it to the PEOC; only the
 *  PEOC issues the alert (PERP §6). Returns the record + the PEOC routing outcome. */
export async function requestPublicAlert(
  input: RequestPublicAlertInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<PublicAlertRequestResult> {
  const body: PublicAlertRequestCreate = {
    session_id: sessionId,
    authority_kind: input.authority_kind,
    requesting_authority_name: input.requesting_authority_name,
    requester_name: input.requester_name,
    incident_id: input.incident_id,
    evacuation_order_id: input.evacuation_order_id,
    hazard_type: input.hazard_type,
    notes: input.notes,
    call_confirmed: input.call_confirmed ?? false,
    call_notes: input.call_notes,
  };
  const res = await fetch('/api/public-alert-requests', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `requestPublicAlert failed ${res.status}`));
  return (await res.json()) as PublicAlertRequestResult;
}

/** RESCIND a public-alert request (POST /:id/rescind → rescind_public_alert_request verb).
 *  Reverses the request + routes a withdrawal notice to the PEOC. */
export async function rescindPublicAlertRequest(
  id: string,
  sessionId: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<PublicAlertRequestResult> {
  const res = await fetch(`/api/public-alert-requests/${id}/rescind`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, reason }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `rescindPublicAlertRequest failed ${res.status}`));
  return (await res.json()) as PublicAlertRequestResult;
}

/* ------------------------------------------------------------------ *
 * #445-1 — Air-support REQUEST to the MNRF/AFFES fire centre (records-not-confers).
 * Clones the public-alert-request client. The COP RECORDS the request + routes it to
 * the fire centre; only MNRF/AFFES tasks the aircraft (PERP §6.7.1). NEVER a dispatch.
 * ------------------------------------------------------------------ */

export interface RequestAirSupportInput {
  requesting_authority_kind: AirSupportAuthorityKind;
  requesting_authority_name: string;
  requester_name?: string;
  incident_id?: string;
  requested_resource: AirSupportRequestedResource;
  hazard_type?: string;
  notes?: string;
}

/** List air-support requests (agency-scoped). */
export async function fetchAirSupportRequests(
  signal?: AbortSignal,
): Promise<AirSupportRequestRecord[]> {
  const res = await fetch('/api/air-support-requests', { signal, cache: 'no-store' });
  if (!res.ok)
    throw new Error(await errorMessage(res, `fetchAirSupportRequests failed ${res.status}`));
  return ((await res.json()) as { requests: AirSupportRequestRecord[] }).requests;
}

/** REQUEST air support (POST /api/air-support-requests → request_air_support verb). The COP
 *  RECORDS the request on behalf of the authority + routes it to the MNRF/AFFES fire centre;
 *  only the fire centre tasks the aircraft (PERP §6.7.1). Returns the record + routing outcome. */
export async function requestAirSupport(
  input: RequestAirSupportInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<AirSupportRequestResult> {
  const body: AirSupportRequestCreate = {
    session_id: sessionId,
    requesting_authority_kind: input.requesting_authority_kind,
    requesting_authority_name: input.requesting_authority_name,
    requester_name: input.requester_name,
    incident_id: input.incident_id,
    requested_resource: input.requested_resource,
    hazard_type: input.hazard_type,
    notes: input.notes,
  };
  const res = await fetch('/api/air-support-requests', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `requestAirSupport failed ${res.status}`));
  return (await res.json()) as AirSupportRequestResult;
}

/** RESCIND an air-support request (POST /:id/rescind → rescind_air_support_request verb). */
export async function rescindAirSupportRequest(
  id: string,
  sessionId: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<AirSupportRequestResult> {
  const res = await fetch(`/api/air-support-requests/${id}/rescind`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, reason }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `rescindAirSupportRequest failed ${res.status}`));
  return (await res.json()) as AirSupportRequestResult;
}

/** STAND DOWN an air-support request (POST /:id/stand-down → stand_down_air_support_request verb). */
export async function standDownAirSupportRequest(
  id: string,
  sessionId: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<AirSupportRequestResult> {
  const res = await fetch(`/api/air-support-requests/${id}/stand-down`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, reason }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `standDownAirSupportRequest failed ${res.status}`));
  return (await res.json()) as AirSupportRequestResult;
}

/* ------------------------------------------------------------------ *
 * #450 — resident public-information release (the #420b resident half)
 * ------------------------------------------------------------------ */

/** Pre-fill source: generate the incident's Public Information Draft (the #300 surface's
 *  endpoint), so the EIO can seed the resident release from it. authedFetch (seat header) —
 *  the draft is per-operator budget-metered. The compose surface strips the [n] markers. */
export async function draftPublicInfoForResident(
  incidentId: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<SitrepDraftResult> {
  const res = await authedFetch('/api/sitreps/draft-public-info', {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ incident_id: incidentId, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `draftPublicInfoForResident failed ${res.status}`));
  return (await res.json()) as SitrepDraftResult;
}

/** All communities (id + name), for the resident-release target picker when the incident has
 *  no in-zone community (a public-information release is not gated on a protective-action zone).
 *  Reuses GET /api/communities (bbox optional → the full roster). */
export async function fetchCommunityPickList(
  signal?: AbortSignal,
): Promise<{ id: string; name: string }[]> {
  const res = await fetch('/api/communities?limit=1000', { signal });
  if (!res.ok)
    throw new Error(await errorMessage(res, `fetchCommunityPickList failed ${res.status}`));
  const fc = (await res.json()) as CommunityFeatureCollection;
  return fc.features
    .map((f) => ({ id: f.properties.id, name: f.properties.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** PUBLISH a resident public-information release to opted-in residents of one community
 *  (publish_public_information verb). The municipality ISSUES its OWN release — NOT Alert Ready. */
export async function publishPublicInfo(
  body: PublicInfoPublishRequest,
  signal?: AbortSignal,
): Promise<PublicInfoPublishResult> {
  const res = await fetch('/api/public-info/publish', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `publishPublicInfo failed ${res.status}`));
  return (await res.json()) as PublicInfoPublishResult;
}

/* ------------------------------------------------------------------ *
 * R2/#478 — protective-action link model (evac = incident attribute)
 * ------------------------------------------------------------------ */

/** Candidate incidents for the "Link to incident" dropdown — open + recently-
 *  deactivated, nearest the alert first (a deactivated one reignites on link). */
export async function fetchLinkableIncidents(
  kind: ProtectiveActionKind,
  alertId: string,
  signal?: AbortSignal,
): Promise<LinkableIncident[]> {
  const res = await fetch(
    `/api/protective-actions/linkable-incidents?kind=${encodeURIComponent(kind)}&alert_id=${encodeURIComponent(alertId)}`,
    { signal, cache: 'no-store' },
  );
  if (!res.ok) throw new Error(`fetchLinkableIncidents failed ${res.status}`);
  return ((await res.json()) as { incidents: LinkableIncident[] }).incidents;
}

/** MANUAL link (dropdown) — collapses the evac/CAP into the incident as a protective
 *  action; linking to a deactivated incident REIGNITES it (deactivated → confirmed). */
export async function linkAlertToIncident(
  kind: ProtectiveActionKind,
  alertId: string,
  incidentId: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<ProtectiveActionLinkResponse> {
  const res = await fetch('/api/protective-actions/link', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      kind,
      alert_id: alertId,
      incident_id: incidentId,
      session_id: sessionId,
    }),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `linkAlertToIncident failed ${res.status}`));
  return (await res.json()) as ProtectiveActionLinkResponse;
}

/** Always-reversible UNLINK — detaches and suppresses re-auto-link (the mis-bind fix). */
export async function unlinkAlertFromIncident(
  kind: ProtectiveActionKind,
  alertId: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<ProtectiveActionLinkResponse> {
  const res = await fetch('/api/protective-actions/unlink', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ kind, alert_id: alertId, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `unlinkAlertFromIncident failed ${res.status}`));
  return (await res.json()) as ProtectiveActionLinkResponse;
}

/** Declare the next operational period on an incident (declare_operational_period
 *  + set_operational_period_cadence when a cadence is given). `sitrep_cadence_minutes`
 *  null/omitted = the v3.2 midpoint+final default. */
export async function declareOperationalPeriod(
  incidentId: string,
  body: {
    start_at: string;
    end_at: string;
    sitrep_cadence_minutes?: number | null;
    session_id: string;
  },
  signal?: AbortSignal,
): Promise<OperationalPeriod> {
  const res = await fetch(`/api/incidents/${incidentId}/operational-periods`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `declareOperationalPeriod failed ${res.status}`));
  return (await res.json()) as OperationalPeriod;
}

/** Advance a period along the forward-only spine (planned→active→closed)
 *  → transition_operational_period verb. */
export async function transitionOperationalPeriod(
  periodId: string,
  status: OperationalPeriodStatus,
  sessionId: string,
  signal?: AbortSignal,
): Promise<OperationalPeriod> {
  const res = await fetch(`/api/operational-periods/${periodId}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ status, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `transitionOperationalPeriod failed ${res.status}`));
  return (await res.json()) as OperationalPeriod;
}

/** Set/clear a period's SITREP auto-issuance cadence (cadence-only PATCH; null
 *  clears back to midpoint+final) → set_operational_period_cadence verb. */
export async function setOperationalPeriodCadence(
  periodId: string,
  cadenceMinutes: number | null,
  sessionId: string,
  signal?: AbortSignal,
): Promise<OperationalPeriod> {
  const res = await fetch(`/api/operational-periods/${periodId}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ sitrep_cadence_minutes: cadenceMinutes, session_id: sessionId }),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `setOperationalPeriodCadence failed ${res.status}`));
  return (await res.json()) as OperationalPeriod;
}

/* ------------------------------------------------------------------ *
 * SITREP engine (SITREP.1) — auto-generated Situation Reports.
 * ------------------------------------------------------------------ */

/** Generate a SITREP for a scope (region bbox or selected incident). Returns the
 *  persisted report incl. the rendered markdown + structured_data. */
export async function createSitrep(
  body: SitrepRequest,
  signal?: AbortSignal,
): Promise<SitrepReport> {
  const res = await fetch('/api/sitreps', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`createSitrep failed ${res.status}`);
  return (await res.json()) as SitrepReport;
}

/** Retrieve a persisted SITREP by id (full markdown + structured_data). */
export async function fetchSitrep(id: string, signal?: AbortSignal): Promise<SitrepReport> {
  const res = await fetch(`/api/sitreps/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchSitrep failed ${res.status}`);
  return (await res.json()) as SitrepReport;
}

/** List recent SITREPs (compact items — no markdown). Pass a `sessionId` to scope
 *  to one operator's session; omit it for the GLOBAL newest-first list so operator
 *  B sees operator A's reports across a shift handover (#142). */
export async function fetchSitreps(
  sessionId?: string,
  limit = 5,
  signal?: AbortSignal,
): Promise<SitrepListResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (sessionId) params.set('session_id', sessionId);
  const res = await fetch(`/api/sitreps?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchSitreps failed ${res.status}`);
  return (await res.json()) as SitrepListResponse;
}

/** #148 Shift Log — the grouped "what changed since shift start" feed over the
 *  operator_actions audit trail. `since` is the shift-start anchor (ISO-8601);
 *  `include` defaults to state changes only (the handover-appropriate slice). */
export async function fetchShiftLog(
  since: string,
  opts: { include?: ShiftLogInclude; limit?: number } = {},
  signal?: AbortSignal,
): Promise<ShiftLogSummary> {
  const params = new URLSearchParams({ since });
  if (opts.include) params.set('include', opts.include);
  if (opts.limit) params.set('limit', String(opts.limit));
  const res = await fetch(`/api/operator-actions/log?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchShiftLog failed ${res.status}`);
  return (await res.json()) as ShiftLogSummary;
}

/** A partner in the Ontario Corps roster, with its capability category. */
export interface OntarioCorpsPartner {
  /** operator_org display name — the key used by hiddenPartners + filtering. */
  name: string;
  /** Capability sub-category (oc_category), or null if uncategorised. */
  category: string | null;
}

/**
 * One-time fetch of the Ontario Corps partner roster (stable subtoggle list),
 * independent of the map bbox. Returns each partner's display name (operator_org)
 * + capability category, used to group the LayerToggle sub-toggles (V.3 §8).
 */
export async function fetchOntarioCorpsPartners(
  signal?: AbortSignal,
): Promise<OntarioCorpsPartner[]> {
  // Province-wide bbox so every partner HQ is included regardless of map view.
  const bbox = { west: -96, south: 41, east: -73, north: 57 };
  const params = new URLSearchParams({
    bbox: bboxParam(bbox),
    asset_class: 'umbrella_org',
    limit: '100',
  });
  const res = await fetch(`/api/response-assets?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchOntarioCorpsPartners failed ${res.status}`);
  const fc = (await res.json()) as ResponseAssetFeatureCollection;
  return fc.features
    .filter((f) => f.properties.parent_org_id !== null && f.properties.operator_org)
    .map((f) => ({
      name: f.properties.operator_org as string,
      category: f.properties.oc_category,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Per-worker ingest heartbeat freshness (#254). Bbox-independent global poll —
 * the COP uses it to surface an honest "live tracking down since X" chip on any
 * layer whose backing daemon has gone dark (see `lib/useFeedHealth` +
 * `deriveLayerFeedHealth`). `cache: 'no-store'` so a dead-feed signal is never
 * served from a stale browser cache.
 */
export async function fetchFeedHealth(signal?: AbortSignal): Promise<FeedHealthResponse> {
  const res = await fetch('/api/feed-health', { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`fetchFeedHealth failed ${res.status}`);
  return (await res.json()) as FeedHealthResponse;
}

/* ------------------------------------------------------------------ *
 * Assistance Request workflow (AR — Task #62) + real forwarding (#79).
 * ------------------------------------------------------------------ */

/** Submit a new assistance request on a requestor's behalf. */
export async function createAssistanceRequest(
  body: AssistanceRequestCreate,
  signal?: AbortSignal,
): Promise<AssistanceRequest> {
  const res = await fetch('/api/assistance-requests', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`createAssistanceRequest failed ${res.status}`);
  return (await res.json()) as AssistanceRequest;
}

/** Triage queue — filterable by status + urgency, or (for the #185 cross-link)
 *  by the raw source record an assist is linked to. */
export async function fetchAssistanceRequests(
  opts: {
    status?: AssistanceStatus;
    urgency?: AssistanceUrgency;
    incident_link_type?: IncidentLinkType;
    incident_link_id?: string;
    /** #419 — scope the list to ONE session's requests (the requestor's "My Requests"
     *  tray). The server filters by session_id; this is the demo requestor key, NOT a
     *  real auth boundary (cross-agency visibility stays deferred to #190). */
    session_id?: string;
    limit?: number;
  } = {},
  signal?: AbortSignal,
): Promise<AssistanceRequestListResponse> {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.urgency) params.set('urgency', opts.urgency);
  if (opts.incident_link_type && opts.incident_link_id) {
    params.set('incident_link_type', opts.incident_link_type);
    params.set('incident_link_id', opts.incident_link_id);
  }
  if (opts.session_id) params.set('session_id', opts.session_id);
  params.set('limit', String(opts.limit ?? 50));
  const res = await fetch(`/api/assistance-requests?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchAssistanceRequests failed ${res.status}`);
  return (await res.json()) as AssistanceRequestListResponse;
}

/** One request, full detail (incl. lifecycle stamps + snapshot). */
export async function fetchAssistanceRequest(
  id: string,
  signal?: AbortSignal,
): Promise<AssistanceRequest> {
  const res = await fetch(`/api/assistance-requests/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchAssistanceRequest failed ${res.status}`);
  return (await res.json()) as AssistanceRequest;
}

/** Advance a request through its lifecycle (triaged/routed/.../closed/cancelled). */
export async function updateAssistanceRequestStatus(
  id: string,
  body: AssistanceStatusUpdate,
  signal?: AbortSignal,
): Promise<AssistanceRequest> {
  const res = await fetch(`/api/assistance-requests/${id}/status`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`updateAssistanceRequestStatus failed ${res.status}`);
  return (await res.json()) as AssistanceRequest;
}

/** #419 closed-loop — PEOC stamps "received" on an inbound request (the requestor
 *  receipt). Orthogonal to status (NO transition), idempotent server-side. Returns the
 *  updated request (now carrying acknowledged_at/acknowledged_by). */
export async function acknowledgeAssistanceRequest(
  id: string,
  sessionId?: string,
  signal?: AbortSignal,
): Promise<AssistanceRequest> {
  const res = await fetch(`/api/assistance-requests/${id}/acknowledge`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(`acknowledgeAssistanceRequest failed ${res.status}`);
  return (await res.json()) as AssistanceRequest;
}

/** #425 LINK — attach (or detach with incidentId=null) a request to a tracked incident
 *  so the request→incident loop closes (requestor sees the incident #; incident sees the
 *  request + its down-tasks). Returns the updated request. */
export async function linkAssistanceRequestToIncident(
  id: string,
  incidentId: string | null,
  sessionId?: string,
  signal?: AbortSignal,
): Promise<AssistanceRequest> {
  const res = await fetch(`/api/assistance-requests/${id}/link-incident`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ incident_id: incidentId, session_id: sessionId }),
    signal,
  });
  if (!res.ok) throw new Error(`linkAssistanceRequestToIncident failed ${res.status}`);
  return (await res.json()) as AssistanceRequest;
}

/** #425 LINK — the assistance requests attached to an incident (incident-side read-back:
 *  routing/forwarding history + issued down-tasks, read-only). */
export async function fetchIncidentAssistanceRequests(
  incidentId: string,
  signal?: AbortSignal,
): Promise<AssistanceRequest[]> {
  const res = await fetch(`/api/incidents/${incidentId}/assistance-requests`, { signal });
  if (!res.ok) throw new Error(`fetchIncidentAssistanceRequests failed ${res.status}`);
  const body = (await res.json()) as { requests: AssistanceRequest[] };
  return body.requests;
}

/**
 * Forward a request to the PEOC duty officer via the real delivery channel.
 *
 * @deprecated (#328c / R4, 2026-07-05) — the operator-facing Forward-to-PEOC affordance is
 * RETIRED; requesting assistance now happens on the inter-ops information-sharing thread
 * (`sendInteropsMessage`, context_type 'assistance_request'). This client stub is kept only for
 * the still-live (deprecated) `POST /:id/forward` route; it has NO web caller. Do NOT wire a new
 * control to it — use the interops thread.
 */
export async function forwardAssistanceRequest(
  id: string,
  sessionId: string,
  destinationId?: string,
  signal?: AbortSignal,
): Promise<DeliveryResult> {
  const res = await fetch(`/api/assistance-requests/${id}/forward`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, destination_id: destinationId }),
    signal,
  });
  if (!res.ok) throw new Error(`forwardAssistanceRequest failed ${res.status}`);
  return (await res.json()) as DeliveryResult;
}

/** Forward a SITREP to a delivery destination via the real delivery channel (#79). */
export async function forwardSitrep(
  id: string,
  sessionId: string,
  destinationId?: string,
  signal?: AbortSignal,
): Promise<DeliveryResult> {
  const res = await fetch(`/api/sitreps/${id}/forward`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, destination_id: destinationId }),
    signal,
  });
  if (!res.ok) throw new Error(`forwardSitrep failed ${res.status}`);
  return (await res.json()) as DeliveryResult;
}

/** Fetch the active delivery destinations registry (migration 0052) for the
 *  forward picker. The default (PEOC) destination sorts first. */
export async function fetchDeliveryDestinations(
  signal?: AbortSignal,
): Promise<DeliveryDestination[]> {
  const res = await fetch('/api/delivery-destinations', { signal });
  if (!res.ok) throw new Error(`fetchDeliveryDestinations failed ${res.status}`);
  const body = (await res.json()) as { destinations: DeliveryDestination[] };
  return body.destinations;
}

/* ------------------------------------------------------------------ *
 * R.4.1 — Resource requests (IMS Resource Status Lifecycle)
 * ------------------------------------------------------------------ */

/** Raise a new resource request (also fired by R.3 "Recommend Dispatch"). */
export async function createResourceRequest(
  body: ResourceRequestCreate,
  signal?: AbortSignal,
): Promise<ResourceRequest> {
  const res = await fetch('/api/resource-requests', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`createResourceRequest failed ${res.status}`);
  return (await res.json()) as ResourceRequest;
}

/** List resource requests, filterable by incident / community / hotspot / status. */
export async function fetchResourceRequests(
  opts: {
    incident_id?: string;
    community_id?: string;
    hotspot_id?: string;
    request_status?: ResourceRequestStatus;
    urgency?: ResourceUrgency;
    limit?: number;
  } = {},
  signal?: AbortSignal,
): Promise<ResourceRequestListResponse> {
  const params = new URLSearchParams();
  if (opts.incident_id) params.set('incident_id', opts.incident_id);
  if (opts.community_id) params.set('community_id', opts.community_id);
  if (opts.hotspot_id) params.set('hotspot_id', opts.hotspot_id);
  if (opts.request_status) params.set('request_status', opts.request_status);
  if (opts.urgency) params.set('urgency', opts.urgency);
  params.set('limit', String(opts.limit ?? 50));
  const res = await fetch(`/api/resource-requests?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchResourceRequests failed ${res.status}`);
  return (await res.json()) as ResourceRequestListResponse;
}

/** One resource request, full detail (incl. linked incident + community + asset). */
export async function fetchResourceRequest(
  id: string,
  signal?: AbortSignal,
): Promise<ResourceRequest> {
  const res = await fetch(`/api/resource-requests/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchResourceRequest failed ${res.status}`);
  return (await res.json()) as ResourceRequest;
}

/** A resource-request lifecycle transition. The action maps 1:1 to the PATCH
 *  path; R.4.2 adds check-in / stage / demobilize to R.4.1's assign/commit/cancel. */
export type ResourceRequestAction =
  | 'assign'
  | 'check-in'
  | 'stage'
  | 'commit'
  | 'demobilize'
  | 'cancel';

/** Advance a resource request through its lifecycle. `stage` requires
 *  staging_area_id; `cancel`/`demobilize` accept an optional reason. */
export async function transitionResourceRequest(
  id: string,
  action: ResourceRequestAction,
  body: {
    session_id: string;
    asset_id?: string;
    staging_area_id?: string;
    cancellation_reason?: string;
    demobilization_reason?: string;
  },
  signal?: AbortSignal,
): Promise<ResourceRequest> {
  const res = await fetch(`/api/resource-requests/${id}/${action}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`transitionResourceRequest(${action}) failed ${res.status}`);
  return (await res.json()) as ResourceRequest;
}

/* ------------------------------------------------------------------ *
 * R.4.2 — Staging areas (IMS Resource Status Lifecycle: the `staging` state)
 * ------------------------------------------------------------------ */

/** Declare a new staging area on an incident. */
export async function createStagingArea(
  body: StagingAreaCreate,
  signal?: AbortSignal,
): Promise<StagingArea> {
  const res = await fetch('/api/staging-areas', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`createStagingArea failed ${res.status}`);
  return (await res.json()) as StagingArea;
}

/** List staging areas for an incident (open only unless include_closed). */
export async function fetchStagingAreas(
  opts: { incident_id: string; include_closed?: boolean; limit?: number },
  signal?: AbortSignal,
): Promise<StagingAreaListResponse> {
  const params = new URLSearchParams();
  params.set('incident_id', opts.incident_id);
  if (opts.include_closed) params.set('include_closed', 'true');
  params.set('limit', String(opts.limit ?? 50));
  const res = await fetch(`/api/staging-areas?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchStagingAreas failed ${res.status}`);
  return (await res.json()) as StagingAreaListResponse;
}

/** §4.4 Evacuation Logistics Surface for an incident — the assembled read model
 *  (linked communities + headcount, staging, aviation assets + airlift aircraft,
 *  overlapping CAP alerts). One GET; the panel is a pure render. */
export async function fetchEvacuationSurface(
  incidentId: string,
  signal?: AbortSignal,
): Promise<EvacuationSurface> {
  const res = await fetch(`/api/incidents/${incidentId}/evacuation`, { signal });
  if (!res.ok) throw new Error(`fetchEvacuationSurface failed ${res.status}`);
  return (await res.json()) as EvacuationSurface;
}

/** Open staging areas intersecting a bbox — the ambient map layer's fetcher. */
export async function fetchStagingAreasInBbox(
  bbox: BboxQuery,
  signal?: AbortSignal,
): Promise<StagingAreaListResponse> {
  const params = new URLSearchParams();
  params.set('bbox', bboxParam(bbox));
  params.set('limit', '500');
  const res = await fetch(`/api/staging-areas?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchStagingAreasInBbox failed ${res.status}`);
  return (await res.json()) as StagingAreaListResponse;
}

/** One staging area, with the bound resource-request count. */
export async function fetchStagingArea(id: string, signal?: AbortSignal): Promise<StagingArea> {
  const res = await fetch(`/api/staging-areas/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchStagingArea failed ${res.status}`);
  return (await res.json()) as StagingArea;
}

/** Soft-close a staging area. */
export async function closeStagingArea(
  id: string,
  body: { session_id: string },
  signal?: AbortSignal,
): Promise<StagingArea> {
  const res = await fetch(`/api/staging-areas/${id}/close`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`closeStagingArea failed ${res.status}`);
  return (await res.json()) as StagingArea;
}

/* ------------------------------------------------------------------ *
 * Task #64 — Sectoring overlay (operator-drawn zones + spatial rollup)
 * ------------------------------------------------------------------ */

/** Read an `{ error, message }` body from a non-OK response, for a friendly toast. */
async function errorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string; error?: string };
    return body.message ?? body.error ?? fallback;
  } catch {
    return fallback;
  }
}

/** Draw a new sector polygon on an incident. */
export async function createSector(body: SectorCreate, signal?: AbortSignal): Promise<Sector> {
  const res = await fetch('/api/sectors', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `createSector failed ${res.status}`));
  return (await res.json()) as Sector;
}

/** List sectors for an incident (open only unless include_closed). */
export async function fetchSectors(
  opts: { incident_id: string; include_closed?: boolean; limit?: number },
  signal?: AbortSignal,
): Promise<SectorListResponse> {
  const params = new URLSearchParams();
  params.set('incident_id', opts.incident_id);
  if (opts.include_closed) params.set('include_closed', 'true');
  params.set('limit', String(opts.limit ?? 200));
  const res = await fetch(`/api/sectors?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchSectors failed ${res.status}`);
  return (await res.json()) as SectorListResponse;
}

/** One sector with its full spatial rollup (anomalies / resources / cameras / communities inside). */
export async function fetchSectorDetail(id: string, signal?: AbortSignal): Promise<SectorDetail> {
  const res = await fetch(`/api/sectors/${id}`, { signal });
  if (!res.ok) throw new Error(`fetchSectorDetail failed ${res.status}`);
  return (await res.json()) as SectorDetail;
}

/** Operator redraws a sector's polygon. */
export async function updateSectorGeometry(
  id: string,
  body: { session_id: string; geometry: Polygon },
  signal?: AbortSignal,
): Promise<Sector> {
  const res = await fetch(`/api/sectors/${id}/geometry`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `updateSectorGeometry failed ${res.status}`));
  return (await res.json()) as Sector;
}

/** Edit a sector's name / lead_label / notes. */
export async function updateSectorMetadata(
  id: string,
  body: { session_id: string; name?: string; lead_label?: string | null; notes?: string | null },
  signal?: AbortSignal,
): Promise<Sector> {
  const res = await fetch(`/api/sectors/${id}/metadata`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `updateSectorMetadata failed ${res.status}`));
  return (await res.json()) as Sector;
}

/** Soft-close a sector (terminal). */
export async function closeSector(
  id: string,
  body: { session_id: string },
  signal?: AbortSignal,
): Promise<Sector> {
  const res = await fetch(`/api/sectors/${id}/close`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res, `closeSector failed ${res.status}`));
  return (await res.json()) as Sector;
}

/* ------------------------------------------------------------------ *
 * #445-(3) — Operator-declared evacuation orders (the municipal NO-GO closer).
 * Uses authedFetch so the operator's seat rides as x-operator-id and the server
 * stamps it as the recorder (created_by_operator_id + the audit operator) — the
 * COP RECORDS the order on behalf of the named issuing authority.
 * ------------------------------------------------------------------ */

/**
 * #457 — derive the "evacuate this community" evacuation zone from the community's
 * own geometry (service_area → StatCan boundary → radius), WITH provenance. The
 * operator reviews/adjusts the returned polygon, then declares via
 * declareEvacuationOrder — the one-click municipal path (no hand-draw).
 */
export async function fetchCommunityEvacuationZone(
  communityId: string,
  opts?: { radiusM?: number },
  signal?: AbortSignal,
): Promise<CommunityEvacuationZone> {
  const qs = opts?.radiusM != null ? `?radius_m=${encodeURIComponent(String(opts.radiusM))}` : '';
  const res = await authedFetch(`/api/communities/${communityId}/evacuation-zone${qs}`, { signal });
  if (!res.ok)
    throw new Error(await errorMessage(res, `fetchCommunityEvacuationZone failed ${res.status}`));
  return (await res.json()) as CommunityEvacuationZone;
}

/** Declare (record) an operator evacuation order — draws + structured authority. */
export async function declareEvacuationOrder(
  body: OperatorEvacuationOrderCreate,
  signal?: AbortSignal,
): Promise<OperatorEvacOrderFeature> {
  const res = await authedFetch('/api/evacuation-orders', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `declareEvacuationOrder failed ${res.status}`));
  return (await res.json()) as OperatorEvacOrderFeature;
}

/** Escalate an evacuation ALERT to an ORDER. */
export async function escalateEvacuationOrder(
  id: string,
  body: { session_id: string },
  signal?: AbortSignal,
): Promise<OperatorEvacOrderFeature> {
  const res = await authedFetch(`/api/evacuation-orders/${id}/escalate`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `escalateEvacuationOrder failed ${res.status}`));
  return (await res.json()) as OperatorEvacOrderFeature;
}

/** Rescind (lift) an operator evacuation order. */
export async function rescindEvacuationOrder(
  id: string,
  body: { session_id: string; reason?: string },
  signal?: AbortSignal,
): Promise<OperatorEvacOrderFeature> {
  const res = await authedFetch(`/api/evacuation-orders/${id}/rescind`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `rescindEvacuationOrder failed ${res.status}`));
  return (await res.json()) as OperatorEvacOrderFeature;
}

/** #462 — begin the §4.30 evacuation ACCOUNTING for this order by establishing the
 *  persisted order→incident link the rollup keys on (attach to a proximate open incident,
 *  else originate one from the order with the operator-picked hazard). Idempotent. */
export async function startEvacuationAccounting(
  id: string,
  body: OperatorEvacuationOrderStartAccounting,
  signal?: AbortSignal,
): Promise<OperatorEvacuationOrderStartAccountingResult> {
  const res = await authedFetch(`/api/evacuation-orders/${id}/start-accounting`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `startEvacuationAccounting failed ${res.status}`));
  return (await res.json()) as OperatorEvacuationOrderStartAccountingResult;
}

/** #459 — correct an operator evac order's AREA (redraw the polygon). */
export async function patchEvacuationOrderGeometry(
  id: string,
  body: OperatorEvacuationOrderGeometryPatch,
  signal?: AbortSignal,
): Promise<OperatorEvacOrderFeature> {
  const res = await authedFetch(`/api/evacuation-orders/${id}/geometry`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `patchEvacuationOrderGeometry failed ${res.status}`));
  return (await res.json()) as OperatorEvacOrderFeature;
}

/** #459/#458 — correct an operator evac order's RECORD (name / population / notes /
 *  issuing + coordinating attribution). Send only the fields being changed. */
export async function patchEvacuationOrderMetadata(
  id: string,
  body: OperatorEvacuationOrderMetadataPatch,
  signal?: AbortSignal,
): Promise<OperatorEvacOrderFeature> {
  const res = await authedFetch(`/api/evacuation-orders/${id}/metadata`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `patchEvacuationOrderMetadata failed ${res.status}`));
  return (await res.json()) as OperatorEvacOrderFeature;
}

/** Active operator-declared evac orders in the bbox (GeoJSON FeatureCollection). */
export async function fetchOperatorEvacuationOrders(
  bbox: { west: number; south: number; east: number; north: number },
  signal?: AbortSignal,
): Promise<OperatorEvacOrderFeatureCollection> {
  const params = new URLSearchParams({
    west: String(bbox.west),
    south: String(bbox.south),
    east: String(bbox.east),
    north: String(bbox.north),
  });
  const res = await authedFetch(`/api/evacuation-orders?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`fetchOperatorEvacuationOrders failed ${res.status}`);
  return (await res.json()) as OperatorEvacOrderFeatureCollection;
}

/* ------------------------------------------------------------------ *
 * B.1 — Briefing-Up view + tokenized URL + subscriptions
 * ------------------------------------------------------------------ */

/** GET the generated Briefing-Up snapshot for the current map view (operator mode). */
export async function fetchBriefingSnapshot(
  bbox: BboxQuery,
  signal?: AbortSignal,
): Promise<BriefingSnapshot> {
  const res = await fetch(`/api/briefing/snapshot?bbox=${bboxParam(bbox)}`, { signal });
  if (!res.ok) throw new Error(`fetchBriefingSnapshot failed ${res.status}`);
  return (await res.json()) as BriefingSnapshot;
}

/** GET the read-only Briefing-Up payload for a token (the Minister's-office path). */
export async function fetchBriefingTokenPayload(
  token: string,
  signal?: AbortSignal,
): Promise<BriefingSnapshot> {
  const res = await fetch(`/api/briefing-tokens/${encodeURIComponent(token)}/payload`, { signal });
  if (!res.ok) {
    const err = new Error(`fetchBriefingTokenPayload failed ${res.status}`) as Error & {
      status?: number;
    };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as BriefingSnapshot;
}

/** Mint a tokenized read-only Briefing-Up URL. */
export async function createBriefingToken(body: BriefingTokenCreate): Promise<BriefingToken> {
  const res = await fetch('/api/briefing-tokens', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`createBriefingToken failed ${res.status}`);
  return (await res.json()) as BriefingToken;
}

/** List existing briefing tokens (admin). */
export async function listBriefingTokens(signal?: AbortSignal): Promise<BriefingToken[]> {
  const res = await fetch('/api/briefing-tokens', { signal });
  if (!res.ok) throw new Error(`listBriefingTokens failed ${res.status}`);
  return ((await res.json()) as { tokens: BriefingToken[] }).tokens;
}

/** Revoke a briefing token before its TTL. */
export async function revokeBriefingToken(
  token: string,
  body: { session_id: string },
): Promise<void> {
  const res = await fetch(`/api/briefing-tokens/${encodeURIComponent(token)}`, {
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`revokeBriefingToken failed ${res.status}`);
}

/** List active email subscribers for an incident's briefings. */
export async function listBriefingSubscriptions(
  incidentId: string,
  signal?: AbortSignal,
): Promise<BriefingSubscription[]> {
  const res = await fetch(
    `/api/briefing-subscriptions?incident_id=${encodeURIComponent(incidentId)}`,
    { signal },
  );
  if (!res.ok) throw new Error(`listBriefingSubscriptions failed ${res.status}`);
  return ((await res.json()) as { subscriptions: BriefingSubscription[] }).subscriptions;
}

/** Add (or re-activate) a briefing email subscriber on an incident. */
export async function createBriefingSubscription(
  body: BriefingSubscriptionCreate,
): Promise<BriefingSubscription> {
  const res = await fetch('/api/briefing-subscriptions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`createBriefingSubscription failed ${res.status}`);
  return (await res.json()) as BriefingSubscription;
}

/** Soft-unsubscribe a briefing email subscriber. */
export async function deleteBriefingSubscription(id: string): Promise<void> {
  const res = await fetch(`/api/briefing-subscriptions/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`deleteBriefingSubscription failed ${res.status}`);
}

/** Export a SITREP as a PDF (server-side pdfkit). Returns the PDF blob. */
export async function exportSitrepPdf(
  sitrepId: string,
  body: { session_id: string },
): Promise<Blob> {
  const res = await fetch(`/api/sitreps/${sitrepId}/export-pdf`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`exportSitrepPdf failed ${res.status}`);
  return await res.blob();
}

/* ---------------------------------------------------------------- *
 * After-Action Report (#612) — the closing beat of the incident workflow (§8).
 * ---------------------------------------------------------------- */

export async function fetchAfterAction(
  incidentId: string,
  signal?: AbortSignal,
): Promise<AfterActionResponse> {
  const res = await fetch(`/api/incidents/${incidentId}/after-action`, { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`fetchAfterAction failed ${res.status}`);
  return (await res.json()) as AfterActionResponse;
}

export async function openAfterAction(
  incidentId: string,
  sessionId: string,
): Promise<AfterActionResponse> {
  const res = await fetch(`/api/incidents/${incidentId}/after-action/open`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `openAfterAction failed ${res.status}`));
  return (await res.json()) as AfterActionResponse;
}

export async function addAfterActionEntry(
  incidentId: string,
  body: {
    kind: AfterActionEntryKind;
    body: string;
    authored_by: string;
    section?: string | null;
    supersedes?: string | null;
    session_id: string;
  },
): Promise<AfterActionResponse> {
  const res = await fetch(`/api/incidents/${incidentId}/after-action/entries`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `addAfterActionEntry failed ${res.status}`));
  return (await res.json()) as AfterActionResponse;
}

export async function finalizeAfterAction(
  incidentId: string,
  finalizedBy: string,
  sessionId: string,
): Promise<AfterActionResponse> {
  const res = await fetch(`/api/incidents/${incidentId}/after-action/finalize`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ finalized_by: finalizedBy, session_id: sessionId }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `finalizeAfterAction failed ${res.status}`));
  return (await res.json()) as AfterActionResponse;
}

export async function exportAfterActionPdf(
  incidentId: string,
  body: { session_id: string; deliver?: boolean; destination_id?: string | null },
): Promise<Blob> {
  const res = await fetch(`/api/incidents/${incidentId}/after-action/export-pdf`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `exportAfterActionPdf failed ${res.status}`));
  return await res.blob();
}

/**
 * #328 Phase 1 — brief an official OUTSIDE the ops centre with this SITREP. Either
 * emails the rendered SITREP PDF to operator-selected officials (channel 'email',
 * ad-hoc addresses allowed) OR records that an official was reached by phone
 * (channel 'phone'). Both persist an in-COP record (App A #72). Returns the result
 * (delivery channel, record id, "Sent HH:MM" timestamp) — never an acknowledgement.
 */
export async function briefOfficial(
  sitrepId: string,
  body: BriefOfficialRequest,
): Promise<BriefOfficialResult> {
  const res = await fetch(`/api/sitreps/${sitrepId}/brief-official`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`briefOfficial failed ${res.status}`);
  return (await res.json()) as BriefOfficialResult;
}

/**
 * #568 — the persisted official-briefing history for a SITREP: the sent briefs + recorded
 * responses, read back from the scribe trail (App A #72), chronological. Enables the
 * OfficialBriefingBox history strip (results were previously transient component state).
 */
export async function fetchOfficialBriefings(
  sitrepId: string,
  signal?: AbortSignal,
): Promise<OfficialBriefingHistoryResponse> {
  const res = await fetch(`/api/sitreps/${sitrepId}/official-briefings`, { signal });
  if (!res.ok) throw new Error(`fetchOfficialBriefings failed ${res.status}`);
  return (await res.json()) as OfficialBriefingHistoryResponse;
}

/**
 * #568 — record what a briefed official said back (the paste-back half of the briefing-up loop;
 * App A #72 Scribe). Append-only — multiple responses may be recorded. A RECORD, never proof of
 * receipt: the server never fabricates an acknowledgement. Returns the newly recorded entry.
 */
export async function recordOfficialResponse(
  sitrepId: string,
  body: RecordOfficialResponseRequest,
): Promise<RecordOfficialResponseResult> {
  const res = await fetch(`/api/sitreps/${sitrepId}/record-official-response`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`recordOfficialResponse failed ${res.status}`);
  return (await res.json()) as RecordOfficialResponseResult;
}

/**
 * #139 / #141 — Unified search. One call returns two groups: geocoded PLACES
 * (NRCan GeoNames-Canada pass-through, each with a fly-to bbox) and matched
 * operator-domain ENTITIES (cameras / assets / fires / communities / incidents).
 */
export async function searchAll(q: string, signal?: AbortSignal): Promise<SearchResponse> {
  const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, { signal });
  if (!res.ok) throw new Error(`searchAll failed ${res.status}`);
  return (await res.json()) as SearchResponse;
}

/**
 * #272 — Wall risk-tour cursors (PART XI Ruling 2.6): each tour stop's
 * cleared-through cursor, derived read-time from operator_actions (the #119
 * idiom — the latest clear_wall_tour_card row per stop). A stop absent from
 * the map has never been cleared (null cursor → every unacked domain item is
 * card-new).
 */
export async function fetchWallTourCursors(signal?: AbortSignal): Promise<Record<string, string>> {
  const res = await fetch('/api/wall-tour/cursors', { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`fetchWallTourCursors failed ${res.status}`);
  const body = (await res.json()) as { cursors?: Record<string, string> };
  return body.cursors ?? {};
}
